import React, { useEffect, useMemo, useState } from "react";
import {
  Grid,
  TextField,
  Typography,
  CircularProgress,
  Box,
  Button,
  Autocomplete,
  InputAdornment,
  Chip,
  Avatar,
  Divider,
  AvatarGroup,
  Stack,
  Skeleton,
  Tooltip,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { FormikProvider, useFormik } from "formik";
import dayjs from "dayjs";
import AppDatePicker from "../../../Components/DatePicker/AppDatePicker";
import { useNotifier } from "../../../Core/Notifier";
import {
  useGetallLocationQuery,
  useGetallStudentQuery,
  useGetallSubjectQuery,
  useGetallGradeQuery,
  useGetAssignClassFilterQuery,
  useGetGradesAssignStudentQuery,
  useGetTeamQuery,
  useGetallAssignClassesQuery,
  useGetClassEligibleStudentsQuery,
} from "../../../Api/attoDeskApi";
import {
  IClassCreate,
  IStudentLocation,
  ITeam,
  IGrade,
  ISubject,
  ISubjectGradePerent,
  IStudent,
  IClassCreateList,
  ISubjectGradeAssign,
} from "../../../Api/Interface/api.interface";
import { TeachMood, ClassType, WeekDays } from "../../../Core/Enum/enum";
import { getRelationshipLabel } from "../../../Core/util/relationship";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";
import { IoSearchSharp } from "react-icons/io5";
import { styled, useTheme } from "@mui/material/styles";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { capitalizeFirstLetter } from "../../../Components/Validation/capitalizeFirstLetter";
import GroupIcon from "@mui/icons-material/Group";
import SchoolIcon from "@mui/icons-material/School";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import PlaceIcon from "@mui/icons-material/Place";
import ScheduleIcon from "@mui/icons-material/Schedule";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import ViewListIcon from "@mui/icons-material/ViewList";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import FamilyRestroomIcon from "@mui/icons-material/FamilyRestroom";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import LaptopIcon from "@mui/icons-material/Laptop";
import StorefrontIcon from "@mui/icons-material/Storefront";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CheckIcon from "@mui/icons-material/Check";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import LoadingPage from "../../../Components/LoadingDialog/LoadingPage";
import BackDrop from "../../../Components/Loader/BackDrop";
import StudentAvatar from "../../../Components/StudentAvatar";
import BillingSetupSection, {
  BillingClass,
  BillingConfig,
  buildPayloadRow,
  emptyConfig,
  isConfigValid,
} from "./BillingSetupSection";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { useAssignClassStudentWithBillingMutation } from "../../../Api/attoDeskApi";
import { gradeLabelOf, locationLabelOf, studyModeLabelOf, normalizeStudyModeValue } from "../../Student/studentDisplay";
import { buildCreateClassSearch, resolveSafeReturnUrl } from "./assignmentContext";

// ── Styled: Subject pill toggle (student's enrolled subjects) ──
const PillToggle = styled(Box, {
  shouldForwardProp: (prop) => prop !== "selected",
})<{ selected?: boolean }>(({ theme, selected }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "5px 14px",
  borderRadius: 20,
  cursor: "pointer",
  fontSize: 12.5,
  fontWeight: 600,
  transition: "all 0.2s cubic-bezier(0.4,0,0.2,1)",
  // Selected: dark-green border, light-green fill, green text. Unselected: subtle.
  border: `1.5px solid ${selected ? "#15803d" : theme.palette.divider}`,
  backgroundColor: selected ? alpha("#22c55e", 0.12) : "transparent",
  color: selected ? "#15803d" : theme.palette.text.secondary,
  "&:hover": {
    borderColor: selected ? "#166534" : "#15803d",
    backgroundColor: selected ? alpha("#22c55e", 0.18) : alpha("#22c55e", 0.05),
  },
}));

// ── Card surface ──
const SectionCard: React.FC<{ children: React.ReactNode; sx?: any }> = ({ children, sx }) => (
  <Box
    sx={{
      bgcolor: "#fff",
      borderRadius: "16px",
      border: "1px solid rgba(15,23,42,0.08)",
      boxShadow: "0 1px 3px rgba(15,23,42,0.04)",
      overflow: "hidden",
      transition: "box-shadow 0.2s ease",
      "&:hover": { boxShadow: "0 4px 18px rgba(15,23,42,0.06)" },
      ...sx,
    }}
  >
    {children}
  </Box>
);

// ── A labelled attribute cell for the CSS-grid student summary (no MUI Grid) ──
const SummaryCell: React.FC<{ icon: React.ReactNode; label: string; value?: string; href?: string }> = ({
  icon,
  label,
  value,
  href,
}) => (
  <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", minWidth: 0 }}>
    <Box sx={{ color: "text.disabled", display: "flex", mt: "1px", "& svg": { fontSize: 18 } }}>{icon}</Box>
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 10, fontWeight: 700, color: "text.disabled", textTransform: "uppercase", letterSpacing: "0.05em" }}>
        {label}
      </Typography>
      <Typography
        {...(href && value ? { component: "a", href } : {})}
        title={value || ""}
        sx={{
          fontSize: 13,
          fontWeight: 600,
          color: href && value ? "primary.main" : "text.primary",
          textDecoration: "none",
          display: "block",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          "&:hover": href && value ? { textDecoration: "underline" } : {},
        }}
      >
        {value || "—"}
      </Typography>
    </Box>
  </Box>
);


// 24h "HH:mm" → "h:MM AM/PM". Empty inputs return "".
const fmt12h = (t?: string | null) => {
  if (!t) return "";
  const [h, m] = t.split(":").map((n) => Number(n));
  if (Number.isNaN(h) || Number.isNaN(m)) return t;
  const hr = h % 12 || 12;
  const ampm = h >= 12 ? "PM" : "AM";
  return `${hr}:${String(m).padStart(2, "0")} ${ampm}`;
};

// ── Availability helper ──
const getAvailabilityInfo = (current: number, max: number | null) => {
  const capacity = max ?? 10;
  const ratio = current / capacity;
  if (ratio >= 1) return { label: "Full", color: "#ef4444", bg: alpha("#ef4444", 0.1) };
  if (ratio >= 0.75) return { label: "Almost Full", color: "#f59e0b", bg: alpha("#f59e0b", 0.1) };
  return { label: "Available", color: "#22c55e", bg: alpha("#22c55e", 0.1) };
};

const studyMoodLabel = (unit: string) => {
  switch ((unit || "").toLowerCase()) {
    case "online": return "Online";
    case "offline":
    case "in_person":
    case "in-person":
    case "in person":
    case "inperson":
    case "physical":
    case "onsite": return "In-Person";
    case "hybrid":
    case "blended": return "Online + In-Person";
    default: return unit || "";
  }
};

const Index = () => {
  const { id: studentId } = useParams<{ id: string }>();
  // Route modes:
  //   /assign/classes/create                    → generic, student search allowed
  //   /assign/classes/create/student/0          → class-first flow (still generic search)
  //   /assign/classes/create/student/{realId}   → FIXED student, no search
  // "0" and undefined are the "no student chosen yet" sentinels.
  const rawRouteId = studentId ?? "";
  const routeStudentId =
    rawRouteId && rawRouteId !== "0" && rawRouteId !== "undefined" ? rawRouteId : null;
  const fixedStudentMode = !!routeStudentId;

  // Class-first flow passes the target class via ?classId=… (and the clicked
  // session via ?occurrence_id=…) so the class is LOCKED, not chosen.
  const [searchParams] = useSearchParams();
  const preselectClassId = searchParams.get("classId");
  const preselectOccurrenceId = searchParams.get("occurrence_id");
  const theme = useTheme();
  const navigate = useNavigate();
  const { showErrorMessage, showMessage } = useNotifier();
  const [currentSearchQuery, setCurrentSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(currentSearchQuery, 300);
  const [getStaffID, setGetStaffId] = useState(routeStudentId ?? "");

  // Assignment MUST go through the billing-gated path. The old
  // `assignMulti` (POST /classes/assign-student-multi, no billing) was
  // reachable via formik's implicit form submit (Enter key inside any
  // input) and would ship a student→class assignment with zero billing
  // configured. Removed; every assignment now goes through
  // `assignWithBilling` behind the `allBillingValid` gate.
  const [billingByClassId, setBillingByClassId] = useState<Record<number, BillingConfig>>({});
  const [assignWithBilling, { isLoading: isAssigningBilling }] = useAssignClassStudentWithBillingMutation();
  const isAssigningMulti = false; // legacy flag; retained so the disabled-state expressions below don't need editing.
  // Ref to the billing-gated submit function so formik.onSubmit (Enter
  // key) can route through it without a hoist. Populated further down,
  // after `submitInlineAssignment` is defined.
  const submitInlineAssignmentRef = React.useRef<() => void | Promise<void>>(() => {});
  const [selectedSubject, setSelectedSubject] = useState<number[]>([]);
  // ── Class Filters (all backend-driven; "" / [] means "Any") ──
  const [filterDays, setFilterDays] = useState<string[]>([]);
  const [filterStaffId, setFilterStaffId] = useState<string>("");
  const [filterClassType, setFilterClassType] = useState<string>("");
  // Class Mode is an admin-facing filter now — the student's profile mode is
  // never applied server-side. "" (default) means "All". Any canonical value
  // (online | in_person | hybrid) narrows the recommended list.
  const [filterClassMode, setFilterClassMode] = useState<string>("");
  // Tracks which student id the filters were pre-filled for (auto-resets when
  // the selected student changes).
  const filtersPrefilledRef = React.useRef<number | null>(null);
  // Multi-selection: the user can pick MANY classes for the same student before
  // assigning. Each stays an independent class assignment (no merged schedule).
  const [selectedClassIds, setSelectedClassIds] = useState<number[]>([]);
  // "View subjects" modal — a single top-level Dialog driven by state
  // so each card can stay a compact height. Populated on click from
  // the card's already-computed gradeSubjectRows; no extra fetch.
  const [openSubjectsFor, setOpenSubjectsFor] = useState<null | {
    title: string;
    rows: Array<{ grade_id: number; grade: string; subjects: string[]; isStudentGrade: boolean }>;
  }>(null);
  // Two-column billing workspace — which selected class is currently
  // being edited on the right-hand billing panel. `null` = no
  // selection yet (or all removed). Auto-focused to the first
  // incomplete class by an effect below.
  const [activeBillingClassId, setActiveBillingClassId] = useState<number | null>(null);
  // Recommended-class card details modal — the card body is now
  // compact (staff + schedule + location/mode + Good match + Select),
  // and the full compatibility / subjects / capacity breakdown lives
  // in this modal. Opened by clicking the Details button. Populated
  // from the same computed data the card already had — no extra fetch.
  const [openDetailsFor, setOpenDetailsFor] = useState<null | {
    id: number;
    title: string;                // subjectName / label
    staffName: string;
    schedule: string;             // "Friday · 9:00 AM – 12:00 PM"
    location: string;
    mode: string;                 // Online / In-Person / Hybrid
    enrollment: string | null;    // class_type
    capacityLine: string;         // "0/1 enrolled · 1 left"
    reasons: Array<{ label: string; active: boolean }>;
    rows: Array<{ grade_id: number; grade: string; subjects: string[]; isStudentGrade: boolean }>;
    viewClassHref: string;
    isSelected: boolean;
    isConflicting: boolean;
  }>(null);
  // Per-Hybrid-class attendance mode chosen by the admin. Keyed by class id.
  // A Hybrid class MUST have a value here before the assignment can submit.
  const [attendanceModes, setAttendanceModes] = useState<Record<number, "Online" | "In Person">>({});
  // Optional effective start date (class-first flow).
  const [effectiveStartDate, setEffectiveStartDate] = useState<string>("");
  // Class ids we auto-selected on return from Create New Class (so we only
  // toast/scroll once per id).
  const handledCreatedRef = React.useRef<Set<number>>(new Set());
  // Card refs, to scroll a newly created/selected class into view.
  const cardRefs = React.useRef<Record<number, HTMLDivElement | null>>({});

  // ── Queries ──
  // Recommendations are STRICT and backend-authoritative: passing student_id
  // makes the endpoint apply the mandatory grade/program/study-mode/location/
  // capacity filters, so only eligible classes come back. selectedSubject adds
  // the (OR) subject filter. getStaffID holds the current student id (set on the
  // fixed route and on search-select).
  // De-duplicate and coerce to positive ints so the request is unambiguous —
  // the backend applies Match-All when 2+ *distinct* subjects are selected, so
  // an accidental duplicate must not flip Any↔All. Stable-sorted so the RTK
  // Query cache key stays consistent for the same selection.
  const normalizedSelectedSubject = useMemo(
    () => Array.from(new Set(selectedSubject.map((n) => Number(n)).filter((n) => Number.isFinite(n) && n > 0))).sort((a, b) => a - b),
    [selectedSubject]
  );

  // Class-first mode is derived from the URL; compute it here so the heavy
  // "recommended classes" + "all classes map" queries below can skip when the
  // class is already preselected (the class card is a locked summary — no
  // picker, no filters, no multi-select).
  const _classFirstMode = !!preselectClassId && !fixedStudentMode;

  const { data: assignClassFilterData, isLoading: assignClassIsLoading, isFetching: assignClassIsFetching } =
    useGetAssignClassFilterQuery(
      {
        id: "",
        staff_id: filterStaffId,
        location_id: "",
        class_mode: filterClassMode,
        class_type: filterClassType,
        student_id: getStaffID ? String(getStaffID) : "",
        select_grade_subject_ids: normalizedSelectedSubject,
        selected_days: filterDays,
      },
      { skip: _classFirstMode },
    );

  const { data: locationData, isLoading: locationIsLoading } =
    useGetallLocationQuery({ searchText: "" });

  const { data: gradeData } = useGetallGradeQuery({ searchText: "" });

  // Only mount the full-roster lookup in flows that actually need it:
  //   • fixed-student route (handled by the targeted query below), or
  //   • class-first route (handled by useGetClassEligibleStudentsQuery, which
  //     is server-side-searched + eligibility-filtered).
  // The old behaviour fetched the entire student table on every mount — a
  // ~43 s request against the slow dev DB — then the result was discarded in
  // class-first mode because the picker was already using the eligible list.
  const needsFullStudentRoster = !preselectClassId && !fixedStudentMode;
  const { data: studentPerentData, isLoading: studentPerentIsLoading, refetch } =
    useGetallStudentQuery(
      { searchText: currentSearchQuery, id: "" },
      { skip: !needsFullStudentRoster },
    );

  // Fixed-student route: fetch that one student directly. We never fall back to
  // the full list for selection here — the student is fixed by the URL.
  const {
    data: fixedStudentData,
    isFetching: fixedStudentFetching,
    isError: fixedStudentIsError,
    error: fixedStudentError,
  } = useGetallStudentQuery(
    { searchText: "", id: routeStudentId ?? "" },
    { skip: !fixedStudentMode }
  );

  const { data: subjectData, isLoading: subjectIsLoading } =
    useGetallSubjectQuery({ searchText: "" });

  const { data: subjectGradeData, isLoading: subjectGradeIsLoading } =
    useGetGradesAssignStudentQuery({ id: String(getStaffID) });

  // Only needed by the recommended-classes multi-pick UI, which is hidden
  // in class-first mode (the class is already locked by ?classId). Skipping
  // avoids a slow full-list fetch that was returning 500 in local dev.
  const { data: assignClassData, isLoading: assignClassListIsLoading } =
    useGetallAssignClassesQuery({ searchText: "" }, { skip: _classFirstMode });

  const { data: staffData, isLoading: staffIsLoading } =
    useGetTeamQuery({ searchText: "", id: "" });

  // ── Class-first flow (opened from a specific class via ?classId) ──
  // Fetch that class's read-only context + its grade-eligible students so the
  // student list is scoped to the class's grade and the fixed class is shown
  // as a locked summary. Backend still enforces eligibility on assign.
  const classFirstMode = !!preselectClassId && !fixedStudentMode;
  const { data: eligibleData, isFetching: eligibleIsFetching } =
    useGetClassEligibleStudentsQuery(
      { classId: preselectClassId ?? "", search: currentSearchQuery },
      { skip: !classFirstMode }
    );
  const eligiblePayload: any = eligibleData?.data ?? null;
  const classContext: any = eligiblePayload?.class ?? null;
  const classGradeIds: number[] = Array.isArray(classContext?.grade_ids) ? classContext.grade_ids : [];
  const gradeLocked: boolean = !!eligiblePayload?.grade_locked;
  // Existing-but-ineligible search matches (name matched, one or more
  // eligibility rules failed). Backend computes the human-readable reason
  // per row. Empty when no search string was supplied.
  interface IneligibleMatch {
    id: number;
    name: string;
    grade?: string | null;
    subjects?: string[] | null;
    image?: string | null;
    reason: string;
  }
  const ineligibleMatches: IneligibleMatch[] = Array.isArray(eligiblePayload?.ineligible_matches)
    ? (eligiblePayload.ineligible_matches as IneligibleMatch[])
    : [];

  // ── Memos ──
  const studentPerentList = useMemo(() => (studentPerentData?.data ?? []) as IStudent[], [studentPerentData?.data]);
  const locationList = useMemo(() => (locationData?.data ?? []) as IStudentLocation[], [locationData?.data]);
  const subjectDataList: ISubject[] = Array.isArray(subjectData?.data) ? (subjectData?.data as ISubject[]) : [];
  const subjectGRadeDataList: IGrade[] = Array.isArray(subjectGradeData?.data) ? (subjectGradeData?.data as IGrade[]) : [];
  const assignClassDataList: IClassCreateList[] = Array.isArray(assignClassFilterData?.data) ? (assignClassFilterData?.data as IClassCreateList[]) : [];
  const assignClassList = useMemo(() => (assignClassData?.data ?? []) as ISubjectGradeAssign[], [assignClassData?.data]);
  const teamList = useMemo(() => staffData?.data as ITeam[], [staffData?.data]);

  // Eligible staff come from the recommended-classes response — every teacher
  // with ≥1 class matching the current filters, computed staff-agnostically by
  // the backend (assignClassFilte). The dropdown and the class list therefore
  // share ONE query and can never disagree.
  const eligibleStaff = useMemo(
    () =>
      (((assignClassFilterData as any)?.eligible_staff ?? []) as Array<{
        id: number;
        name: string;
        matching_class_count: number;
      }>),
    [assignClassFilterData]
  );

  useEffect(() => {
    if (debouncedSearchQuery.length >= 2 || debouncedSearchQuery === "") {
      setCurrentSearchQuery(debouncedSearchQuery);
      // The full-roster query is now skipped in class-first / fixed-student
      // mode, so refetch() would throw "Cannot refetch a query that has not
      // been started yet." Only refetch when the query is actually active.
      if (needsFullStudentRoster) refetch();
    }
  }, [debouncedSearchQuery]);

  // If the selected teacher stops being eligible after a filter change, reset to
  // "Any Staff" (which re-fetches the full recommended set). Guarded on
  // !isFetching so a transient in-flight empty can't wrongly clear a valid pick.
  useEffect(() => {
    if (!filterStaffId || assignClassIsFetching) return;
    const stillEligible = eligibleStaff.some((s) => String(s.id) === String(filterStaffId));
    if (!stillEligible) setFilterStaffId("");
  }, [eligibleStaff, filterStaffId, assignClassIsFetching]);

  const formik = useFormik<IClassCreate>({
    initialValues: {
      student_id: routeStudentId ?? "",
      location_id: "",
      class_mode: "",
      class_type: "",
      number_of_student: null,
      select_grade_subject_ids: [],
      period_based_from_date: null,
      period_based_to_date: null,
      notes: "",
      selected_days: [],
      startTime: "",
      endTime: "",
      staff_id: "",
      class_id: null,
    },
    enableReinitialize: true,
    // Route all form submissions (explicit button clicks AND implicit
    // Enter-key submits) through the same billing-gated function that
    // the visible CTAs use. The previous direct call to `assignMulti`
    // here bypassed the Billing Setup gate — a student could be
    // assigned to a class without any billing configured, just by
    // pressing Enter inside a form input after auto-selection of a
    // newly-created class.
    onSubmit: async () => {
      await submitInlineAssignmentRef.current();
    },
  });

  // Pre-select the class passed in via ?classId (class-first flow).
  useEffect(() => {
    if (!preselectClassId) return;
    const cid = Number(preselectClassId);
    if (Number.isNaN(cid) || cid <= 0) return;
    setSelectedClassIds((prev) => (prev.includes(cid) ? prev : [...prev, cid]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectClassId]);

  // ── Maps ──
  const studentPerentOptions = useMemo(() => {
    // Class-first flow: use the server-side-filtered eligible students (same
    // endpoint that backs the "Why is this student ineligible?" list below).
    // The backend has already applied grade / mode / location / subject rules
    // and name search, so no client-side filtering is needed. This also means
    // we no longer pay for a full-roster download on this route.
    const eligibleFromServer: any[] = Array.isArray(eligiblePayload?.students)
      ? (eligiblePayload.students as any[])
      : [];
    const eligible = classFirstMode
      ? eligibleFromServer.map((s: any) => ({
          label: String(s.name || "").trim(),
          value: s.id,
          image: s.image || null,
          eligible: true as const,
          reason: undefined as string | undefined,
          grade: s.grade ?? null,
          subjects: undefined as string[] | undefined,
        }))
      : (Array.isArray(studentPerentList) ? studentPerentList : []).map((cust) => ({
          label: `${capitalizeFirstLetter(cust.first_name) || ""} ${capitalizeFirstLetter(cust.middle_name) || ""} ${capitalizeFirstLetter(cust.last_name) || ""}`.trim(),
          value: cust.id,
          image: cust.image || null,
          eligible: true as const,
          reason: undefined as string | undefined,
          grade: (cust as any).grade ?? null,
          subjects: undefined as string[] | undefined,
        }));
    // Append ineligible name-search matches only in the class-first flow —
    // this is the picker where "does this class accept this student" is
    // the whole question. Rows carry `eligible: false` so the Autocomplete
    // can disable selection and render the reason.
    const ineligible = classFirstMode
      ? ineligibleMatches.map((m) => ({
          label: m.name,
          value: m.id,
          image: m.image || null,
          eligible: false as const,
          reason: m.reason,
          grade: m.grade ?? null,
          subjects: Array.isArray(m.subjects) ? m.subjects : [],
        }))
      : [];
    return [...eligible, ...ineligible];
  }, [studentPerentList, classFirstMode, gradeLocked, classGradeIds, ineligibleMatches, eligiblePayload?.students]);

  const subjectMap = useMemo(() => {
    const map = new Map();
    subjectDataList?.forEach((user) => map.set(user.id, user.subject_name));
    return map;
  }, [subjectDataList]);

  const teamMap = useMemo(() => {
    const map = new Map<number, { name: string; image: string }>();
    teamList?.forEach((t) => {
      map.set(t.id, {
        name: `${capitalizeFirstLetter(t.teamFirstName || "")} ${capitalizeFirstLetter(t.middle_name || "")} ${capitalizeFirstLetter(t.teamLastName || "")}`.trim(),
        image: t.teamImg,
      });
    });
    return map;
  }, [teamList]);

  const assignStudentMap = useMemo(() => {
    const map = new Map<number, { name: string; image: string }>();
    studentPerentList?.forEach((s) => {
      map.set(s.id, {
        name: `${capitalizeFirstLetter(s.first_name || "")} ${capitalizeFirstLetter(s.middle_name || "")} ${capitalizeFirstLetter(s.last_name || "")}`.trim(),
        image: s.image,
      });
    });
    return map;
  }, [studentPerentList]);

  const locationMap = useMemo(() => {
    const map = new Map<number, { location_name: string; location_color: string }>();
    locationList?.forEach((l) => map.set(l.id, { location_name: l.location_name, location_color: l.location_color }));
    return map;
  }, [locationList]);

  const gradeMap = useMemo(() => {
    const map = new Map<number, string>();
    ((gradeData?.data ?? []) as IGrade[])?.forEach((g: any) => map.set(g.id, g.grade));
    return map;
  }, [gradeData?.data]);

  // The single student resolved from the fixed-student route.
  const fixedStudent = useMemo(() => {
    const arr = fixedStudentData?.data;
    return Array.isArray(arr) && arr.length > 0 ? (arr[0] as IStudent) : null;
  }, [fixedStudentData?.data]);

  const gradeClassMap = useMemo(() => {
    const map = new Map();
    assignClassList?.forEach((c) => map.set(c.id, c.grade_name));
    return map;
  }, [assignClassList]);

  const assignClassMap = useMemo(() => {
    const map = new Map();
    assignClassList?.forEach((c) => map.set(c.id, c.subject_name));
    return map;
  }, [assignClassList]);

  // Map: subjects_grades_tables.id → grade_id (for grade matching)
  const gradeIdFromSgMap = useMemo(() => {
    const map = new Map<number, number>();
    assignClassList?.forEach((c) => map.set(c.id, c.grade_id));
    return map;
  }, [assignClassList]);

  // ── Selected student (fixed route → URL student, generic → searched) ──
  const selectedStudent = useMemo(() => {
    if (fixedStudentMode) return fixedStudent;
    if (!formik.values.student_id) return null;
    return studentPerentList.find((s) => s.id === Number(formik.values.student_id)) || null;
  }, [fixedStudentMode, fixedStudent, formik.values.student_id, studentPerentList]);


  // ── Derived student-summary display values ──
  const studentFullName = selectedStudent
    ? [selectedStudent.first_name, selectedStudent.middle_name, selectedStudent.last_name]
        .map((p) => capitalizeFirstLetter(p || ""))
        .filter(Boolean)
        .join(" ")
    : "";
  const studentIdLabel = selectedStudent ? selectedStudent.reference_id || `ID #${selectedStudent.id}` : "";
  // Relation-first resolution (see studentDisplay) so a valid grade_id/location_id
  // never renders as "—", even when the permission-scoped picklist omits it.
  const gradeLabel = gradeLabelOf(selectedStudent, gradeMap);
  const locationLabel = locationLabelOf(selectedStudent, locationMap);
  const enrollmentLabel = selectedStudent?.program_label || selectedStudent?.program_type || "";
  const studyModeText = studyModeLabelOf(selectedStudent?.study_mode);
  const isOnlineStudy = normalizeStudyModeValue(selectedStudent?.study_mode) === "online";
  const studentParent =
    selectedStudent?.parent ||
    selectedStudent?.relationships?.find((r) => r.isPrimary)?.parent ||
    selectedStudent?.relationships?.[0]?.parent ||
    selectedStudent?.relationship?.parent ||
    null;
  // Pick the relationship row that pairs with the displayed parent so the
  // label matches what the admin actually chose ("Mother", "Father", "Legal
  // Guardian", …). Falls back to the direct relationship link on the student,
  // then to the first active row. This is the fix for the "always shows
  // GUARDIAN even when Parent was selected" bug — the previous code
  // hardcoded the label to "Guardian" regardless of the stored value.
  const studentParentRelationshipRow: any =
    (studentParent
      ? (selectedStudent?.relationships as any[] | undefined)?.find(
          (r) => r?.parent?.id != null && studentParent?.id != null && r.parent.id === studentParent.id,
        )
      : null) ||
    (selectedStudent as any)?.relationship ||
    ((selectedStudent as any)?.relationships as any[] | undefined)?.[0] ||
    null;
  const parentRelationshipRaw: string | null =
    studentParentRelationshipRow?.relationship_type ?? (studentParent as any)?.relationship_type ?? null;
  const parentRelationshipOther: string | null =
    studentParentRelationshipRow?.relationship_other ?? (studentParent as any)?.relationship_other ?? null;
  const parentRelationshipLabel = getRelationshipLabel(parentRelationshipRaw, parentRelationshipOther);
  const parentName = studentParent
    ? [studentParent.first_name, studentParent.last_name].map((p) => capitalizeFirstLetter(p || "")).filter(Boolean).join(" ")
    : "";
  const parentPhone = studentParent?.contact_number || "";
  const parentEmail = studentParent?.email || "";
  const statusRaw = (selectedStudent?.status || "active").toString();
  const isActiveStudent = statusRaw.toLowerCase() !== "inactive";

  // Distinct enrolled-subject names (from the grade/subject query).
  const enrolledSubjectNames = useMemo(() => {
    const names: string[] = [];
    subjectGRadeDataList.forEach((g: IGrade) =>
      g.selected_sub_array?.forEach((sub: ISubjectGradePerent) => {
        const n = subjectMap.get(sub.subject_id);
        if (n && !names.includes(n)) names.push(n);
      })
    );
    return names;
  }, [subjectGRadeDataList, subjectMap]);

  // How many classes the student is currently enrolled in.
  const currentClassCount = useMemo(() => {
    if (!selectedStudent) return 0;
    return assignClassDataList.filter((c) =>
      (c.assignStudent || []).some((a: any) => a.student_id === selectedStudent.id)
    ).length;
  }, [assignClassDataList, selectedStudent?.id]);

  // Per-class match badges. The backend returns an authoritative, ID-based
  // `match` object for every eligible class — prefer it so a badge can never
  // claim a match the backend didn't make (no label/text-fragment matching).
  // The client computation is only a fallback for the no-student generic view.
  const buildMatchReasons = (item: IClassCreateList) => {
    const m = (item as any).match;
    if (m) {
      // Only surface a badge for a dimension the student actually HAS — e.g. a
      // student with no enrollment set never shows an "Enrollment match" badge.
      const reasons: { label: string; active: boolean }[] = [];
      if (selectedStudent?.grade_id) reasons.push({ label: "Grade match", active: !!m.grade });
      if (enrolledSubjectNames.length > 0) reasons.push({ label: "Subject match", active: !!m.subject });
      if (selectedStudent?.program_type) reasons.push({ label: "Enrollment match", active: !!m.program });
      if (selectedStudent?.study_mode) reasons.push({ label: "Study mode match", active: !!m.study_mode });
      // Location is no longer a match dimension.
      reasons.push({ label: "Capacity available", active: !!m.capacity });
      return reasons;
    }
    const classGradeIds = new Set(
      (item.select_grade_subject_ids || []).map((id) => gradeIdFromSgMap.get(id)).filter(Boolean)
    );
    const classSubjectNames = new Set(
      (item.select_grade_subject_ids || []).map((id) => assignClassMap.get(id)).filter(Boolean)
    );
    const count = item.assignStudent?.length || 0;
    return [
      { label: "Grade match", active: !!selectedStudent?.grade_id && classGradeIds.has(selectedStudent.grade_id) },
      { label: "Subject match", active: enrolledSubjectNames.some((n) => classSubjectNames.has(n)) },
      {
        label: "Study mode match",
        active:
          !!selectedStudent?.study_mode &&
          !!item.class_mode &&
          selectedStudent.study_mode.toLowerCase() === item.class_mode.toLowerCase(),
      },
      { label: "Capacity available", active: !item.number_of_student || count < item.number_of_student },
    ];
  };

  // Which scheduled days a class runs on (days with at least one time slot).
  const classDays = (c: IClassCreateList): Set<string> => {
    const days = new Set<string>();
    (c.availability || []).forEach((d: any) => {
      if ((d.timeSlots?.length ?? 0) > 0) days.add(d.day);
    });
    return days;
  };

  // The backend already returned ONLY eligible, not-already-enrolled classes for
  // the active filters. We rank strongest-match first using the requested
  // priority: grade > subject > program > study mode > location > day > staff >
  // class type > capacity (each a higher bit so ties break by priority).
  const recommendedClasses = useMemo(() => {
    const score = (c: IClassCreateList) => {
      const m = (c as any).match || {};
      let s = 0;
      // Ranking priority (highest bit wins ties): student's exact grade first,
      // then how many selected subjects the class matches, then program /
      // study mode / etc. A grade-matched class always ranks above a same-
      // subject class at another grade; among grade-matched classes, more
      // matched selected subjects wins.
      if (selectedStudent?.grade_id && m.grade) s += 1 << 16;
      const matchedSubjectCount = Number(m.matched_subject_count) || 0;
      s += matchedSubjectCount * (1 << 12);
      if (selectedStudent?.program_type && m.program) s += 1 << 7;
      if (selectedStudent?.study_mode && m.study_mode) s += 1 << 6;
      if (filterDays.length > 0) {
        const days = classDays(c);
        if (filterDays.some((d) => days.has(d))) s += 1 << 4;
      }
      if (filterStaffId && String(c.staff_id) === filterStaffId) s += 1 << 3;
      if (filterClassType && c.class_type === filterClassType) s += 1 << 2;
      const cnt = c.assignStudent?.length || 0;
      if (!c.number_of_student || cnt < c.number_of_student) s += 1 << 1;
      return s;
    };
    return [...assignClassDataList].sort((a, b) => score(b) - score(a));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignClassDataList, selectedStudent?.id, enrolledSubjectNames, selectedSubject, filterDays, filterStaffId, filterClassType]);

  // Class-first flow: the class is LOCKED — keep exactly the one class selected
  // and never let it be dropped or added to.
  useEffect(() => {
    if (!classFirstMode || !preselectClassId) return;
    const cid = Number(preselectClassId);
    if (!cid || Number.isNaN(cid)) return;
    setSelectedClassIds((prev) => (prev.length === 1 && prev[0] === cid ? prev : [cid]));
  }, [classFirstMode, preselectClassId]);

  // Persist selections across recommendation refreshes / filter changes. Only
  // DROP a selected class once we're sure it is no longer a valid recommendation
  // (loaded, not mid-refetch, and absent from the list) — never during loading.
  // Skipped in the class-first flow, where the class is locked regardless of the
  // recommendation list.
  useEffect(() => {
    if (classFirstMode) return;
    if (assignClassIsLoading || assignClassIsFetching) return;
    setSelectedClassIds((prev) => {
      const valid = prev.filter((id) => recommendedClasses.some((c) => c.id === id));
      return valid.length === prev.length ? prev : valid;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recommendedClasses, assignClassIsLoading, assignClassIsFetching, classFirstMode]);

  // Subject filters start EMPTY on load — an empty selection is treated as
  // "Any Subject" and the backend already scopes recommendations to the
  // student's enrolled subjects at their grade (server-authoritative). The
  // student summary chips are informational only and must never pre-select
  // filters. When the selected student changes we reset the filters once so
  // stale picks from a previous student don't survive.
  useEffect(() => {
    if (classFirstMode || !selectedStudent) return;
    if (filtersPrefilledRef.current === selectedStudent.id) return;
    setSelectedSubject([]);
    filtersPrefilledRef.current = selectedStudent.id;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStudent?.id, classFirstMode]);

  // ── Filter handlers ──
  const toggleDay = (day: string) =>
    setFilterDays((prev) => (prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]));

  const clearFilters = () => {
    setSelectedSubject([]);
    setFilterDays([]);
    setFilterStaffId("");
    setFilterClassType("");
    setFilterClassMode("");
    // Keep the prefill from immediately re-applying for this student.
    filtersPrefilledRef.current = selectedStudent?.id ?? -1;
  };

  const activeFilterCount =
    (selectedSubject.length > 0 ? 1 : 0) +
    (filterDays.length > 0 ? 1 : 0) +
    (filterStaffId ? 1 : 0) +
    (filterClassType ? 1 : 0) +
    (filterClassMode ? 1 : 0);

  // Study Mode belongs to the individual class, not the student profile — a
  // student can attend Online, In-Person and Hybrid classes. Assignment must
  // never be gated on an empty profile mode.
  const hasStudyMode = true;

  // Returning from "Create New Class": auto-select the new class ONCE it appears
  // in the (refetched) recommendations, scroll it into view, and toast. Existing
  // manual selections are preserved. If the new class is not eligible for this
  // student, warn instead of silently selecting it.
  // Both `created_class_id` (returned from Create Class round-trip) and
  // `selected_class_id` (pushed by Class Schedules → Continue to Assign
  // Student) preselect exactly one class. Same auto-select-once behaviour
  // for either — SelectStudentDialog uses `selected_class_id` so the two
  // params can safely coexist (they will never both be present in the same
  // URL because they originate from different entry points).
  const createdClassId = searchParams.get("created_class_id") || searchParams.get("selected_class_id");
  useEffect(() => {
    if (!createdClassId) return;
    const cid = Number(createdClassId);
    if (!cid || Number.isNaN(cid) || handledCreatedRef.current.has(cid)) return;
    // Wait until recommendations have finished (re)loading so we don't act on a
    // stale list that predates the new class.
    if (assignClassIsLoading || assignClassIsFetching) return;

    if (recommendedClasses.some((c) => c.id === cid)) {
      handledCreatedRef.current.add(cid);
      setSelectedClassIds((prev) => (prev.includes(cid) ? prev : [...prev, cid]));
      showMessage("Class created and selected successfully.");
      setTimeout(() => {
        cardRefs.current[cid]?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 200);
    } else {
      // Loaded, not fetching, and still absent → not eligible for this student.
      handledCreatedRef.current.add(cid);
      showErrorMessage(
        "The new class was created, but it isn't eligible for this student, so it was not selected."
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createdClassId, recommendedClasses, assignClassIsLoading, assignClassIsFetching]);

  // ── Handlers ──
  const handleSubjectChange = (categoryId: number) => {
    setSelectedSubject((prev) =>
      prev.includes(categoryId) ? prev.filter((id) => id !== categoryId) : [...prev, categoryId]
    );
  };

  // Toggle a class in/out of the multi-selection — never assigns immediately.
  // Blocks selecting a class that conflicts with an already-selected one so
  // click / keyboard / button paths all share the same guard (visual disabled
  // state alone can be bypassed via keyboard or programmatic events).
  const toggleClassSelect = (classId: number) => {
    setSelectedClassIds((prev) => {
      if (prev.includes(classId)) {
        return prev.filter((id) => id !== classId);
      }
      if (conflictByClassId.has(classId)) {
        return prev;
      }
      return [...prev, classId];
    });
  };
  const removeSelectedClass = (classId: number) => {
    setSelectedClassIds((prev) => prev.filter((id) => id !== classId));
    setAttendanceModes((prev) => {
      if (!(classId in prev)) return prev;
      const { [classId]: _drop, ...rest } = prev;
      return rest;
    });
  };
  const clearAllSelected = () => {
    setSelectedClassIds([]);
    setAttendanceModes({});
  };

  // Open Create New Class carrying the student-assignment context, so the form
  // pre-fills from the student and returns to THIS Assign Class page after a
  // successful create. Falls back to a plain create when no student is chosen.
  const openCreateClass = () => {
    const sid = selectedStudent?.id ?? (routeStudentId ? Number(routeStudentId) : null);
    if (!sid) {
      navigate("/assign/classes/create");
      return;
    }
    const search = buildCreateClassSearch({
      studentId: sid,
      gradeId: selectedStudent?.grade_id,
      programType: selectedStudent?.program_type,
      studyMode: selectedStudent?.study_mode,
      locationId: selectedStudent?.location_id,
      // Pre-fill the subject only when exactly one chip is selected.
      subjectSgId: selectedSubject.length === 1 ? selectedSubject[0] : null,
    });
    navigate(`/assign/classes/create?${search}`);
  };

  const getClassScheduleSummary = (availability: any[]) => {
    const days: string[] = [];
    let startTime = "";
    let endTime = "";
    (availability || []).forEach((day) => {
      if (day.timeSlots.length > 0) {
        days.push(day.day);
        if (!startTime && !endTime) {
          startTime = day.timeSlots[0].startTime;
          endTime = day.timeSlots[0].endTime;
        }
      }
    });
    return { days, startTime, endTime };
  };

  const cancelFunction = () => {
    clearAllSelected();
    setSelectedSubject([]);
  };

  const goToProfile = () => {
    const pid = selectedStudent?.id ?? routeStudentId;
    if (pid) navigate(`/student-history/${pid}`);
    else navigate(-1);
  };

  // Assign is enabled once a real student is resolved and at least one class is
  // selected. Payment setup lives on the student Profile/Payments area.
  const formValid = useMemo(() => {
    const sid = Number(formik.values.student_id);
    if (!formik.values.student_id || formik.values.student_id === "0" || Number.isNaN(sid) || sid <= 0) return false;
    if (selectedClassIds.length === 0) return false;
    // Every selected Hybrid class must have an attendance mode picked. The
    // backend will reject otherwise; blocking here avoids a round-trip.
    const hybridClasses = selectedClassIds
      .map((id) => recommendedClasses.find((c) => c.id === id))
      .filter((c: any) => c && String(c.class_mode || "").toLowerCase() === "hybrid");
    return hybridClasses.every((c: any) => !!attendanceModes[c!.id]);
  }, [formik.values.student_id, selectedClassIds, recommendedClasses, attendanceModes]);

  // ── Selected-class derivations (for the sticky summary + conflict status) ──
  const selectedClasses = useMemo(
    () => selectedClassIds
      .map((id) => recommendedClasses.find((c) => c.id === id))
      .filter(Boolean) as IClassCreateList[],
    [selectedClassIds, recommendedClasses]
  );

  // Grid ordering for the recommended-classes section: selected cards go to
  // the top (preserving the recommendation order they had when they were
  // selected), then every unselected card in its original ranked order.
  // Pure view-layer; the server-returned list is never mutated and the
  // submit payload reads from selectedClassIds, so this is UX-only.
  const availableRecommendedClasses = useMemo(
    () => recommendedClasses.filter((c) => !selectedClassIds.includes(c.id)),
    [recommendedClasses, selectedClassIds],
  );
  const orderedRecommendedClasses = useMemo(
    () => [...selectedClasses, ...availableRecommendedClasses],
    [selectedClasses, availableRecommendedClasses],
  );

  // Initialise a billing config for every selected class (once). We NEVER
  // overwrite an existing entry — that would nuke manual edits when the
  // recommended-classes query refetches or a filter changes the surrounding
  // data. Deselected classes are pruned so stale state doesn't leak.
  React.useEffect(() => {
    setBillingByClassId((prev) => {
      const next: Record<number, BillingConfig> = {};
      selectedClassIds.forEach((id) => {
        next[id] = prev[id] ?? emptyConfig(id);
      });
      return next;
    });
  }, [selectedClassIds]);

  const updateBilling = React.useCallback((class_id: number, patch: Partial<BillingConfig>) => {
    setBillingByClassId((prev) => {
      const existing = prev[class_id];
      // Any field change other than the completion flag itself flips
      // `is_completed` back to false so the admin re-confirms the numbers.
      // Prevents "Billing complete" chips from silently outliving edits.
      const patchTouchesFields = Object.keys(patch).some((k) => k !== "is_completed");
      const merged: BillingConfig = {
        ...existing,
        ...patch,
        ...(patchTouchesFields && patch.is_completed === undefined
          ? { is_completed: false }
          : {}),
      } as BillingConfig;
      return { ...prev, [class_id]: merged };
    });
  }, []);

  const applyBillingToAll = React.useCallback((source: BillingConfig) => {
    setBillingByClassId((prev) => {
      const next: Record<number, BillingConfig> = {};
      Object.keys(prev).forEach((idStr) => {
        const id = Number(idStr);
        // Copy the source config but wipe completion — the target classes
        // haven't been individually confirmed yet.
        next[id] = { ...prev[id], ...source, class_id: id, is_completed: false };
      });
      return next;
    });
  }, []);

  const billingConfigList = React.useMemo(
    () => selectedClassIds.map((id) => billingByClassId[id]).filter(Boolean) as BillingConfig[],
    [selectedClassIds, billingByClassId],
  );
  // Field-level validity (used to decide when to *enable* the per-class
  // Complete Billing Setup button inside the section).
  const billingValidCount = billingConfigList.filter(isConfigValid).length;
  // Explicit completion (used to gate the final "Assign Student" button).
  // Requires the admin to have pressed Complete Billing Setup on each card.
  const billingCompleteCount = billingConfigList.filter((c) => c.is_completed && isConfigValid(c)).length;
  const allBillingValid = billingConfigList.length > 0 && billingCompleteCount === billingConfigList.length;

  // Per-class billing state used by the two-column workspace to render
  // a status chip on each selected-class card and to decide which
  // class the right-hand billing panel focuses next.
  const isClassBillingComplete = React.useCallback((id: number): boolean => {
    const c = billingByClassId[id];
    return !!c && !!c.is_completed && isConfigValid(c);
  }, [billingByClassId]);
  const isClassBillingValid = React.useCallback((id: number): boolean => {
    const c = billingByClassId[id];
    return !!c && isConfigValid(c);
  }, [billingByClassId]);

  // Keep `activeBillingClassId` valid & useful:
  //   * When selections change, focus the first incomplete class.
  //   * When the active class becomes complete, auto-advance to the
  //     next incomplete class (matches "Save & Next" behaviour without
  //     duplicating the per-card Complete Billing Setup logic).
  //   * When the active class is removed, fall through to the same
  //     first-incomplete rule.
  React.useEffect(() => {
    if (selectedClassIds.length === 0) {
      if (activeBillingClassId !== null) setActiveBillingClassId(null);
      return;
    }
    const stillSelected = activeBillingClassId != null && selectedClassIds.includes(activeBillingClassId);
    const firstIncomplete = selectedClassIds.find((id) => !isClassBillingComplete(id)) ?? selectedClassIds[0];
    if (!stillSelected) {
      setActiveBillingClassId(firstIncomplete);
      return;
    }
    // Active class is complete AND at least one other is still incomplete → advance.
    if (isClassBillingComplete(activeBillingClassId!) && firstIncomplete !== activeBillingClassId) {
      setActiveBillingClassId(firstIncomplete);
    }
  }, [selectedClassIds, isClassBillingComplete, activeBillingClassId]);

  // ── Canonical readiness gates ─────────────────────────────────────
  // One place where every requirement for enabling the final Assign
  // button is computed. Any UI surface that renders a "why is this
  // disabled" hint reads from THIS object so the summary can't drift
  // from the button's disabled condition (the previous regression:
  // "Billing complete · No conflicts" but the button was still greyed
  // out because Hybrid attendance mode wasn't picked and the summary
  // didn't say so).
  const hybridSelectedClasses = useMemo(
    () => selectedClasses.filter((c: any) => String(c?.class_mode || "").toLowerCase() === "hybrid"),
    [selectedClasses],
  );
  const hybridAttendanceComplete = hybridSelectedClasses.filter((c: any) => !!attendanceModes[c.id]).length;
  const attendanceModesComplete =
    hybridSelectedClasses.length === 0 || hybridAttendanceComplete === hybridSelectedClasses.length;
  const missingAttendanceCount = hybridSelectedClasses.length - hybridAttendanceComplete;
  const firstIncompleteAttendanceClassId = hybridSelectedClasses.find(
    (c: any) => !attendanceModes[c.id],
  )?.id as number | undefined;

  // Derive the canonical `attendance_mode` value per assignment.
  // Non-Hybrid classes auto-resolve from their teaching mode (Online-only
  // → online, In-Person-only → in_person) so the field is always populated
  // when the backend needs it. Hybrid classes take the admin's pick.
  const attendanceModeFor = (cls: any): "online" | "in_person" | null => {
    const mode = String(cls?.class_mode || "").toLowerCase();
    if (mode === "online") return "online";
    if (mode === "inperson" || mode === "in_person" || mode === "in-person" || mode === "in person" || mode === "offline") return "in_person";
    if (mode === "hybrid") {
      const picked = attendanceModes[cls.id];
      if (picked === "Online") return "online";
      if (picked === "In Person") return "in_person";
    }
    return null;
  };

  // Direct inline submit — replaces the old openFeeModal path. Same API
  // contract as the modal (assignMultiWithBilling) and the same shared
  // payload builder so the two surfaces cannot drift apart.
  const submitInlineAssignment = async () => {
    if (!formValid || hasSelectionConflict || !allBillingValid || !attendanceModesComplete) {
      // Scroll the first incomplete Hybrid class into view and highlight
      // its attendance picker so the user knows exactly what's blocking.
      if (!attendanceModesComplete && firstIncompleteAttendanceClassId) {
        document
          .getElementById(`assign-attendance-required-${firstIncompleteAttendanceClassId}`)
          ?.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }
    try {
      const resp: any = await assignWithBilling({
        student_id: Number(formik.values.student_id),
        classes: billingConfigList.map((c) => {
          const clsMeta = selectedClasses.find((s) => s.id === c.class_id);
          return {
            ...buildPayloadRow(c),
            invoice_generation_preference: c.invoice_generation_preference,
            // attendance_mode is required for Hybrid classes and derived
            // automatically for single-mode classes so the backend can
            // persist it consistently for every assignment.
            attendance_mode: attendanceModeFor(clsMeta),
          };
        }),
      }).unwrap();
      if (!resp?.status) {
        showErrorMessage(resp?.message || "Assignment failed");
        return;
      }
      showMessage(resp.message);
      const invoiceNumber = resp?.data?.invoice?.invoiceNumber ?? null;
      const count = Number(resp?.data?.count ?? billingConfigList.length);
      if (invoiceNumber) {
        showMessage(`Assigned to ${count} ${count === 1 ? "class" : "classes"} — invoice ${invoiceNumber} created.`);
      }
      setSelectedClassIds([]);
      setSelectedSubject([]);
      setBillingByClassId({});
      const sid = formik.values.student_id;
      // When the caller supplied a validated `return_url` (e.g. Class
      // Schedules → Continue to Assign Student), honour it — that is the
      // whole point of the round-trip. Otherwise fall back to the prior
      // per-mode default so the classic Student Profile flow still lands
      // on the profile, and the Class Sessions flow still lands on the
      // list.
      const requestedReturn = searchParams.get("return_url");
      const safeReturn = resolveSafeReturnUrl(requestedReturn, "");
      // fixedStudentMode arriving from the class-first flow carries
      // `selected_class_id`; without a valid return_url, fall back to
      // that specific class page — not the generic list.
      const selectedClassIdParam = searchParams.get("selected_class_id");
      const classOriginFallback = selectedClassIdParam ? `/class-schedules/${selectedClassIdParam}` : `/class-schedule/list`;
      const fallback = classFirstMode
        ? `/class-schedule/list`
        : (fixedStudentMode && selectedClassIdParam ? classOriginFallback : `/student-history/${sid}`);
      navigate(safeReturn || fallback);
    } catch (e: any) {
      showErrorMessage(e?.data?.message || "Assignment failed");
    }
  };

  // Keep the ref pointing at the current closure so formik.onSubmit
  // (which fires on implicit form submit — e.g. Enter key inside an
  // input) routes through the billing-gated path. Assigned on every
  // render because submitInlineAssignment closes over changing state.
  submitInlineAssignmentRef.current = submitInlineAssignment;


  // What the modal renders as its per-class rows. Built from the same shapes
  // the sticky "Selected Classes" panel already uses so labels match.
  const feeModalClasses: BillingClass[] = useMemo(() => {
    return selectedClasses.map((item) => {
      const liveStaffFee = (item as any).staff as { full_name?: string | null; image?: string | null } | null | undefined;
      const staffGet = liveStaffFee?.full_name
        ? { name: liveStaffFee.full_name, image: liveStaffFee.image || "" }
        : teamMap.get(item.staff_id);
      const locData = locationMap.get(item.location_id);
      const { days, startTime, endTime } = getClassScheduleSummary(item.availability);
      const subjectName =
        Array.from(new Set(item.select_grade_subject_ids.map((id) => assignClassMap.get(id)).filter(Boolean))).join(", ") || `Class #${item.id}`;
      const isOnline = (item.class_mode || "").toLowerCase() === "online";
      const scheduleText = `${days.length ? days.map((d) => d.substring(0, 3)).join(", ") + " · " : ""}${startTime && endTime ? `${startTime}–${endTime}` : ""}`;
      return {
        id: item.id,
        label: subjectName,
        staff: staffGet?.name,
        schedule: scheduleText,
        mode: `${studyMoodLabel(item.class_mode)}${!isOnline && locData?.location_name ? " · " + locData.location_name : ""}`,
      };
    });
  }, [selectedClasses, teamMap, locationMap, assignClassMap]);

  // Every scheduled day/time slot of a class (all days, not just the first).
  const slotsOfClass = (item: IClassCreateList) => {
    const out: { day: string; start: string; end: string }[] = [];
    (item.availability || []).forEach((d: any) =>
      (d.timeSlots || []).forEach((t: any) =>
        out.push({ day: d.day, start: String(t.startTime).slice(0, 5), end: String(t.endTime).slice(0, 5) })
      )
    );
    return out;
  };

  // For each recommended class, does it overlap ANY currently-selected class?
  // Used to disable conflicting cards in the grid so the user can never pick two
  // classes that share a weekday and time range. Back-to-back (end == start) is
  // allowed. Already-selected cards are never marked conflicting — that would
  // let a self-conflict pin them in the "disabled" state. Backend re-validates
  // on submit, so this is UX only.
  // Shared handler for a click on a card / Select button that we've marked as
  // "would create a time conflict". Rather than silently ignoring the click
  // (as the previous version did) we surface a specific reason via the global
  // notifier so the user knows exactly what to do next.
  const notifyConflictClick = (conflict: { day: string; start: string; end: string } | undefined) => {
    const dayLabel = conflict?.day ? conflict.day.charAt(0).toUpperCase() + conflict.day.slice(1).toLowerCase() : "the same time";
    const timeLabel = conflict ? `${fmt12h(conflict.start)}–${fmt12h(conflict.end)}` : "";
    showErrorMessage(
      "A class at the same time is already selected. Unselect the other class first.",
      conflict ? `Overlaps ${dayLabel} ${timeLabel}.` : undefined,
    );
  };

  const conflictByClassId = useMemo(() => {
    const map = new Map<number, { day: string; start: string; end: string }>();
    if (selectedClasses.length === 0) return map;
    const selectedSlots = selectedClasses.map((c) => ({ id: c.id, slots: slotsOfClass(c) }));
    for (const item of recommendedClasses) {
      if (selectedClassIds.includes(item.id)) continue;
      const mySlots = slotsOfClass(item);
      let hit: { day: string; start: string; end: string } | null = null;
      for (const sel of selectedSlots) {
        for (const s of sel.slots) {
          for (const m of mySlots) {
            if (m.day === s.day && m.start < s.end && m.end > s.start) {
              hit = { day: s.day, start: s.start, end: s.end };
              break;
            }
          }
          if (hit) break;
        }
        if (hit) break;
      }
      if (hit) map.set(item.id, hit);
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClasses, selectedClassIds, recommendedClasses]);

  // Client-side conflict preview among the SELECTED classes (same day, overlapping
  // time; back-to-back allowed). The backend re-validates authoritatively on submit.
  const selectionConflicts = useMemo(() => {
    const pairs: { a: IClassCreateList; b: IClassCreateList }[] = [];
    for (let i = 0; i < selectedClasses.length; i++) {
      for (let j = i + 1; j < selectedClasses.length; j++) {
        const sa = slotsOfClass(selectedClasses[i]);
        const sb = slotsOfClass(selectedClasses[j]);
        const clash = sa.some((x) => sb.some((y) => x.day === y.day && x.start < y.end && x.end > y.start));
        if (clash) pairs.push({ a: selectedClasses[i], b: selectedClasses[j] });
      }
    }
    return pairs;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClasses]);
  const hasSelectionConflict = selectionConflicts.length > 0;

  // Full-page loader for the supporting datasets. Only block on queries that
  // have NO cached data yet — once the static-lookup caches warm up (see
  // keepUnusedDataFor on getallLocation / getallSubject / getTeam / grade),
  // this evaluates to false and the shell renders instantly. The old gate
  // blocked the entire route every time one of these flipped `isLoading`,
  // even when the cached response was already in the store from a prior
  // visit, so clicking Add Student from Class Detail always showed a
  // full-screen Step Up loader.
  const pageIsLoading =
    (locationIsLoading && !locationData) ||
    (subjectIsLoading && !subjectData) ||
    (staffIsLoading && !staffData) ||
    (assignClassListIsLoading && !assignClassData) ||
    (!fixedStudentMode && !preselectClassId && studentPerentIsLoading && !studentPerentData);

  if (pageIsLoading) {
    return <LoadingPage open={pageIsLoading} />;
  }

  const permissionDenied = fixedStudentIsError && (fixedStudentError as any)?.status === 403;
  const notFound =
    fixedStudentMode && !fixedStudentFetching && !fixedStudentIsError && !selectedStudent;

  const studentSummaryCard = selectedStudent && (
    <SectionCard>
      <Box sx={{ p: { xs: 2, md: 2.5 } }}>
        {/* Deliberate responsive grid — identity is wide, attributes are sized to
            their content so the row stays compact with no giant blank gaps. */}
        <Box
          sx={{
            display: "grid",
            alignItems: "center",
            columnGap: { xs: 2, md: "28px" },
            rowGap: 2,
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "minmax(230px, 1.4fr) minmax(120px, 0.7fr) minmax(190px, 1.1fr) minmax(130px, 0.75fr) minmax(140px, 0.8fr) auto",
            },
          }}
        >
          {/* Identity — avatar + name (one line) with ID and Active on a compact row */}
          <Box
            sx={{
              display: "flex",
              gap: 1.75,
              alignItems: "center",
              minWidth: 0,
              // A calm separator that never touches the name (padding + border,
              // full-height vertical Divider removed).
              pr: { md: 3 },
              borderRight: { md: "1px solid rgba(15,23,42,0.08)" },
            }}
          >
            <StudentAvatar student={selectedStudent} size={56} fallbackLabel={studentFullName} />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                title={studentFullName || "Student"}
                sx={{
                  fontSize: 17,
                  fontWeight: 700,
                  lineHeight: 1.25,
                  whiteSpace: "nowrap",   // never break first/last name onto two lines
                  overflow: "hidden",
                  textOverflow: "ellipsis", // only ellipsizes at genuinely narrow widths
                }}
              >
                {studentFullName || "Student"}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.4, flexWrap: "wrap" }}>
                <Typography sx={{ fontSize: 12.5, color: "text.secondary", whiteSpace: "nowrap" }}>
                  {studentIdLabel}
                </Typography>
                <Chip
                  size="small"
                  icon={<Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: isActiveStudent ? "#22c55e" : "#94a3b8", ml: "8px !important" }} />}
                  label={isActiveStudent ? "Active" : "Inactive"}
                  sx={{
                    height: 20,
                    fontSize: 11,
                    fontWeight: 700,
                    bgcolor: isActiveStudent ? alpha("#22c55e", 0.1) : alpha("#94a3b8", 0.14),
                    color: isActiveStudent ? "#16a34a" : "#64748b",
                    "& .MuiChip-label": { px: 0.75 },
                  }}
                />
              </Box>
            </Box>
          </Box>

          {/* Primary attributes — sized by the grid, not stretched.
              "School Grade" is informational only: it does NOT control which
              academic classes the student can attend (see ACADEMIC LEVELS
              below the Subjects row). Study Mode and Location are properties
              of each class the student attends, not permanent restrictions,
              so they are omitted from this summary. */}
          <SummaryCell icon={<SchoolIcon />} label="School Grade" value={gradeLabel} />
          <SummaryCell icon={<WorkspacePremiumIcon />} label="Enrollment Type" value={enrollmentLabel} />
          {/* Study Mode and Location live on each class, not on the student
              profile. If profile values aren't set we say so explicitly rather
              than showing a dash that looks like missing required data. */}
          <SummaryCell icon={isOnlineStudy ? <LaptopIcon /> : <StorefrontIcon />} label="Study Mode" value={studyModeText || "Varies by class"} />
          <SummaryCell icon={<PlaceIcon />} label="Location" value={locationLabel || "Varies by class"} />

          {/* Action — far right, no giant gap before it */}
          <Box sx={{ gridColumn: { xs: "1 / -1", sm: "1 / -1", md: "auto" }, justifySelf: { md: "end" } }}>
            <Button
              variant="outlined"
              size="small"
              fullWidth
              startIcon={<VisibilityOutlinedIcon sx={{ fontSize: "16px !important" }} />}
              onClick={goToProfile}
              sx={{ textTransform: "none", fontWeight: 600, borderRadius: "10px", whiteSpace: "nowrap" }}
            >
              View Profile
            </Button>
          </Box>
        </Box>

        {/* Secondary metadata — compact wrapped row, so it never distorts the primary grid */}
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: { xs: 2, md: "28px" }, mt: 2 }}>
          <SummaryCell icon={<ViewListIcon />} label="Classes" value={`${currentClassCount} enrolled`} />
          {parentName && <SummaryCell icon={<FamilyRestroomIcon />} label={parentRelationshipLabel} value={parentName} />}
          {parentPhone && <SummaryCell icon={<PhoneOutlinedIcon />} label="Phone" value={parentPhone} href={`tel:${parentPhone}`} />}
          {parentEmail && <SummaryCell icon={<MailOutlineIcon />} label="Email" value={parentEmail} href={`mailto:${parentEmail}`} />}
        </Box>

        {/* Academic Levels & Subjects — grouped BY grade, since a student can
            legitimately study across multiple academic grades regardless of
            their default school grade. Each chip is a distinct grade+subject
            pair (e.g. "Grade 4 · Mathematics"), so a Grade 2 school-grade
            student who is assigned Grade 4 Mathematics sees exactly that. */}
        {subjectGRadeDataList.length > 0 && (
          <>
            <Divider sx={{ my: 1.75 }} />
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 0.75 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "text.disabled", mr: 0.5 }}>
                <SchoolIcon sx={{ fontSize: 16 }} />
                <Typography sx={{ fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Academic Levels
                </Typography>
              </Box>
              {subjectGRadeDataList
                .filter((g: IGrade) => (g.selected_sub_array?.length ?? 0) > 0)
                .map((g: IGrade) => (
                <Chip
                  key={`al-${g.id}`}
                  size="small"
                  label={g.grade}
                  sx={{
                    height: 20,
                    fontSize: 11,
                    fontWeight: 700,
                    bgcolor: alpha(theme.palette.primary.main, 0.09),
                    color: theme.palette.primary.main,
                    "& .MuiChip-label": { px: 0.9 },
                  }}
                />
              ))}
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "text.disabled", mr: 0.5 }}>
                <MenuBookIcon sx={{ fontSize: 16 }} />
                <Typography sx={{ fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}>
                  Subjects
                </Typography>
              </Box>
              {subjectGRadeDataList.map((item: IGrade) =>
                item.selected_sub_array?.map((sub: ISubjectGradePerent, idx: number) => (
                  <PillToggle
                    key={`${item.id}-${idx}`}
                    selected={selectedSubject.includes(sub.id)}
                    onClick={() => handleSubjectChange(sub.id)}
                  >
                    {`${item.grade} · ${subjectMap.get(sub.subject_id) || "Subject"}`}
                  </PillToggle>
                ))
              )}
            </Box>
          </>
        )}
      </Box>
    </SectionCard>
  );

  return (
    <Box sx={{ bgcolor: "#f8fafc", minHeight: "100vh", mx: -3, mt: -3, p: { xs: 2, md: 3 } }}>
      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit}>
          {/* Outer two-column workspace — on lg+ the right sidebar
              hosts a sticky Billing Setup panel so admins can configure
              billing while browsing the 32 recommended classes. Below
              lg the sidebar collapses; billing continues to live inside
              the Selected Classes section (its native location on
              smaller viewports). */}
          <Box sx={{
            width: "100%",
            display: "flex",
            gap: { xs: 0, lg: 2.5 },
            alignItems: "flex-start",
          }}>
          <Box sx={{ flex: { xs: "1 1 100%", lg: "1 1 auto" }, minWidth: 0, width: { xs: "100%", lg: "auto" } }}>
            <Stack spacing={2.5}>
              {/* ════ HEADER ════ */}
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1.5 }}>
                <Box>
                  <Typography sx={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.02em" }}>
                    {classFirstMode ? "Add Student to Class" : "Assign Class"}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 0.25 }}>
                    {classFirstMode
                      ? "Select an eligible student to add to this class."
                      : "Review the student and assign the best matching class."}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: { xs: "stretch", sm: "flex-end" }, gap: 0.75 }}>
                  <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
                    {(() => {
                      // Back-button destination priority: valid `return_url`
                      // supplied by the caller (e.g. Class Schedules) wins,
                      // otherwise the historical per-mode default. Label
                      // reflects the destination so the admin knows where
                      // Back will send them.
                      const requestedReturn = searchParams.get("return_url");
                      const safeReturn = resolveSafeReturnUrl(requestedReturn, "");
                      const backLabel = safeReturn
                        ? "Back"
                        : (classFirstMode ? "Back to Class Sessions" : "Back to Profile");
                      const backHandler = safeReturn
                        ? () => navigate(safeReturn)
                        : (classFirstMode ? () => navigate("/class-schedule/list") : goToProfile);
                      return (
                        <Button
                          variant="outlined"
                          startIcon={<ArrowBackIcon sx={{ fontSize: "16px !important" }} />}
                          onClick={backHandler}
                          sx={{ textTransform: "none", fontWeight: 600, borderRadius: "10px", px: 2.5 }}
                        >
                          {backLabel}
                        </Button>
                      );
                    })()}
                    {!classFirstMode && selectedClassIds.length > 0 && (
                      <Button
                        variant="text"
                        onClick={cancelFunction}
                        sx={{ textTransform: "none", fontWeight: 600, borderRadius: "10px", px: 2, color: "text.secondary" }}
                      >
                        Reset
                      </Button>
                    )}
                    {classFirstMode && (() => {
                      // Continue to the full Assign Class page for the picked
                      // student, carrying the current class id as a preselect
                      // and forwarding the caller's return_url so success on
                      // the destination lands back at Class Schedules (or
                      // wherever this Add Student page was opened from).
                      // The simplified Add Student page is a picker — Billing
                      // Setup and the final commit live on the destination.
                      const sid = selectedStudent?.id ?? Number(formik.values.student_id) ?? null;
                      const canContinue = classFirstMode && !!preselectClassId && !!sid;
                      const requestedReturn = searchParams.get("return_url");
                      const classFallback = preselectClassId ? `/class-schedules/${preselectClassId}` : "/class-schedules";
                      const safeReturn = resolveSafeReturnUrl(requestedReturn, classFallback);
                      const originSource = searchParams.get("source") || "class-schedule";
                      const goToFullAssign = () => {
                        if (!canContinue) return;
                        const p = new URLSearchParams();
                        p.set("selected_class_id", String(preselectClassId));
                        p.set("source", originSource);
                        p.set("return_url", safeReturn);
                        navigate(`/assign/classes/create/student/${sid}?${p.toString()}`);
                      };
                      return (
                        <Tooltip
                          title={
                            canContinue
                              ? "Open the full Assign Class page for this student with the class preselected — complete Billing Setup and assign there."
                              : "Select an eligible student first."
                          }
                          arrow
                        >
                          <span>
                            <Button
                              variant="contained"
                              // Use the theme's primary token — same navy
                              // used by every other primary CTA on the
                              // page (Assign to N Classes, Save/Create,
                              // Mark Attendance) — instead of the bright
                              // success green, which reads as a confirm
                              // rather than a next-step action.
                              color="primary"
                              disabled={!canContinue}
                              onClick={goToFullAssign}
                              endIcon={<ArrowForwardIcon sx={{ fontSize: "16px !important" }} />}
                              sx={{
                                textTransform: "none",
                                fontWeight: 700,
                                borderRadius: "10px",
                                px: 2.5,
                                boxShadow: "0 2px 8px rgba(15,23,42,0.15)",
                                "&:hover": { boxShadow: "0 4px 16px rgba(15,23,42,0.2)" },
                              }}
                            >
                              Continue to Assign Class
                            </Button>
                          </span>
                        </Tooltip>
                      );
                    })()}
                    <Tooltip
                      title={
                        hasSelectionConflict
                          ? "Resolve the schedule conflict before assigning."
                          : (selectedClasses.length > 0 && !allBillingValid)
                            ? "Complete the billing setup below for every selected class."
                            : (selectedClasses.length > 0 && !attendanceModesComplete)
                              ? `Select Online or In-Person attendance mode for ${missingAttendanceCount} Hybrid ${missingAttendanceCount === 1 ? "class" : "classes"}.`
                              : ""
                      }
                      arrow
                    >
                      <span>
                        <Button
                          variant="contained"
                          onClick={submitInlineAssignment}
                          disabled={!formValid || isAssigningMulti || isAssigningBilling || hasSelectionConflict || (selectedClasses.length > 0 && (!allBillingValid || !attendanceModesComplete))}
                          sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            borderRadius: "10px",
                            px: 3,
                            boxShadow: "0 2px 8px rgba(15,23,42,0.15)",
                            "&:hover": { boxShadow: "0 4px 16px rgba(15,23,42,0.2)" },
                          }}
                        >
                          {(isAssigningMulti || isAssigningBilling) && <CircularProgress size={18} sx={{ mr: 1, color: "inherit" }} />}
                          {classFirstMode
                            ? "Assign Student to This Class"
                            : selectedClassIds.length <= 1
                              ? "Assign to 1 Class"
                              : `Assign to ${selectedClassIds.length} Classes`}
                        </Button>
                      </span>
                    </Tooltip>
                  </Stack>
                </Box>
              </Box>

              {/* ════ CLASS-FIRST: read-only selected-class summary (locked context) ════ */}
              {classFirstMode && classContext && (
                <SectionCard sx={{ borderColor: alpha(theme.palette.primary.main, 0.25) }}>
                  <Box sx={{ p: { xs: 2, md: 2.5 } }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
                        <Box sx={{ width: 38, height: 38, borderRadius: "10px", bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          <MenuBookIcon sx={{ fontSize: 20 }} />
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.disabled", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                            Adding a student to
                          </Typography>
                          <Typography sx={{ fontSize: 16, fontWeight: 700, lineHeight: 1.25, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={classContext.title}>
                            {(classContext.subjects || []).join(", ") || classContext.title || `Class #${classContext.id}`}
                          </Typography>
                        </Box>
                      </Box>
                      <Chip
                        icon={<LockOutlinedIcon sx={{ fontSize: "14px !important" }} />}
                        label="Class context locked"
                        size="small"
                        sx={{ height: 24, fontSize: 11, fontWeight: 700, bgcolor: alpha(theme.palette.primary.main, 0.08), color: theme.palette.primary.main, "& .MuiChip-icon": { color: theme.palette.primary.main }, "& .MuiChip-label": { px: 0.75 } }}
                      />
                    </Box>

                    <Box
                      sx={{
                        display: "grid",
                        gap: { xs: 1.5, md: 2 },
                        gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(3, 1fr)", md: "repeat(4, 1fr)" },
                      }}
                    >
                      <SummaryCell icon={<MenuBookIcon />} label="Subject" value={(classContext.subjects || []).join(", ") || "—"} />
                      <SummaryCell icon={<SchoolIcon />} label="Grade" value={(classContext.grades || []).join(", ") || "—"} />
                      <SummaryCell icon={<GroupIcon />} label="Staff" value={classContext.staff_name || "—"} />
                      <SummaryCell icon={classContext.is_online ? <LaptopIcon /> : <StorefrontIcon />} label="Mode" value={classContext.mode_label || "—"} />
                      <SummaryCell icon={<PlaceIcon />} label="Location" value={classContext.location_name || "—"} />
                      <SummaryCell icon={<ScheduleIcon />} label="Day & Time" value={[classContext.day, classContext.start_time && classContext.end_time ? `${classContext.start_time}–${classContext.end_time}` : ""].filter(Boolean).join(" · ") || "—"} />
                      <SummaryCell
                        icon={<GroupIcon />}
                        label="Capacity"
                        value={
                          classContext.number_of_student
                            ? `${classContext.enrolled_count ?? 0} / ${classContext.number_of_student}`
                            : `${classContext.enrolled_count ?? 0} enrolled`
                        }
                      />
                    </Box>

                    {/* Effective start date (optional) */}
                    <Box sx={{ mt: 2, display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: "text.disabled", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                        Effective start date
                      </Typography>
                      <AppDatePicker
                        value={effectiveStartDate}
                        onChange={setEffectiveStartDate}
                        ariaLabel="Effective start date"
                        fullWidth={false}
                        sx={{ width: 190 }}
                      />
                      <Typography sx={{ fontSize: 11.5, color: "text.disabled" }}>Optional — defaults to today.</Typography>
                    </Box>

                    {gradeLocked && classGradeIds.length > 0 && (
                      <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 1.75, display: "flex", alignItems: "center", gap: 0.75 }}>
                        <ErrorOutlineIcon sx={{ fontSize: 15, color: theme.palette.primary.main }} />
                        Showing only students eligible for this class{classContext.grades?.length ? ` (${classContext.grades.join(", ")})` : ""}. Staff, subject and mode are inherited from the class.
                      </Typography>
                    )}
                  </Box>
                </SectionCard>
              )}

              {/* ════ GENERIC-ROUTE STUDENT SEARCH ════ */}
              {!fixedStudentMode && (
                <SectionCard>
                  <Box sx={{ p: { xs: 2, md: 2.5 } }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                      <PersonSearchIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />
                      <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                        {classFirstMode ? "Select Eligible Student" : "Select Student"}
                      </Typography>
                    </Box>
                    <Autocomplete
                      disableClearable
                      popupIcon={null}
                      forcePopupIcon={false}
                      options={studentPerentOptions}
                      loading={studentPerentIsLoading || eligibleIsFetching}
                      // Block ineligible-row selection at the source. The
                      // renderOption below also visually marks them, but
                      // this stops selection via keyboard/enter and
                      // guarantees `Continue to Assign Class` can never
                      // enable on a non-eligible pick.
                      getOptionDisabled={(option: any) => option?.eligible === false}
                      getOptionLabel={(option: any) => option.label}
                      value={studentPerentOptions.find((opt) => opt.value === Number(formik.values.student_id)) || undefined}
                      onChange={(_, newValue: any) => {
                        if (newValue?.eligible === false) return;
                        const sid = newValue?.value ? String(newValue.value) : "";
                        formik.setFieldValue("student_id", sid);
                        setGetStaffId(sid);
                        setSelectedSubject([]);
                      }}
                      onInputChange={(_, inputValue, reason) => {
                        if (reason === "input") setCurrentSearchQuery(inputValue);
                      }}
                      renderOption={(props, option: any) => {
                        const ineligible = option?.eligible === false;
                        const subjectsLine = Array.isArray(option?.subjects) && option.subjects.length > 0
                          ? option.subjects.join(", ")
                          : null;
                        const gradeLine = option?.grade ? String(option.grade) : null;
                        const contextLine = [gradeLine ? `Grade ${gradeLine}` : null, subjectsLine].filter(Boolean).join(" · ");
                        return (
                          <Box
                            component="li"
                            {...props}
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 1.5,
                              opacity: ineligible ? 0.7 : 1,
                              cursor: ineligible ? "not-allowed !important" : undefined,
                              bgcolor: ineligible ? "#fff7ed" : undefined,
                            }}
                            aria-disabled={ineligible || undefined}
                          >
                            <StudentAvatar student={{ id: option.value, name: option.label, image: option.image }} size={32} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>{option.label}</Typography>
                                {ineligible ? (
                                  <Chip
                                    size="small"
                                    label="Not eligible"
                                    sx={{
                                      height: 20, fontSize: 10.5, fontWeight: 700,
                                      bgcolor: "#fee2e2", color: "#991b1b",
                                      border: "1px solid #fecaca",
                                    }}
                                  />
                                ) : (
                                  <Chip
                                    size="small"
                                    label="Eligible"
                                    sx={{
                                      height: 20, fontSize: 10.5, fontWeight: 700,
                                      bgcolor: "#dcfce7", color: "#166534",
                                      border: "1px solid #bbf7d0",
                                    }}
                                  />
                                )}
                              </Box>
                              {contextLine && (
                                <Typography sx={{ fontSize: 12, color: "#64748b", mt: 0.25 }}>
                                  {contextLine}
                                </Typography>
                              )}
                              {ineligible && option?.reason && (
                                <Typography sx={{ fontSize: 11.5, color: "#9a3412", mt: 0.5 }}>
                                  {option.reason}
                                </Typography>
                              )}
                            </Box>
                          </Box>
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder={classFirstMode ? "Search eligible students by name..." : "Search student by name..."}
                          size="small"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <InputAdornment position="end">
                                {studentPerentIsLoading ? <CircularProgress size={18} /> : <IoSearchSharp style={{ fontSize: 20, color: theme.palette.text.disabled }} />}
                              </InputAdornment>
                            ),
                          }}
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                        />
                      )}
                    />
                  </Box>
                </SectionCard>
              )}

              {/* ════ FIXED-ROUTE STUDENT: loading / error / not-found ════ */}
              {fixedStudentMode && fixedStudentFetching && !selectedStudent && (
                <SectionCard>
                  <Box sx={{ p: 2.5, display: "flex", gap: 2, alignItems: "center" }}>
                    <Skeleton variant="circular" width={58} height={58} />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton width="35%" height={24} />
                      <Skeleton width="20%" height={16} />
                      <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                        {[0, 1, 2, 3].map((i) => <Skeleton key={i} width={80} height={30} />)}
                      </Box>
                    </Box>
                  </Box>
                </SectionCard>
              )}

              {fixedStudentMode && (fixedStudentIsError || notFound) && (
                <SectionCard>
                  <Box sx={{ p: 4, textAlign: "center" }}>
                    {permissionDenied ? (
                      <LockOutlinedIcon sx={{ fontSize: 44, color: "#f59e0b", mb: 1 }} />
                    ) : (
                      <ErrorOutlineIcon sx={{ fontSize: 44, color: "#ef4444", mb: 1 }} />
                    )}
                    <Typography sx={{ fontSize: 16, fontWeight: 700, mb: 0.5 }}>
                      {permissionDenied ? "You don't have access to this student" : "Student not found"}
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 2.5 }}>
                      {permissionDenied
                        ? "You don't have permission to view or assign classes for this student."
                        : "This student could not be loaded. They may have been removed."}
                    </Typography>
                    <Button
                      variant="outlined"
                      startIcon={<ArrowBackIcon />}
                      onClick={() => navigate("/student/list")}
                      sx={{ textTransform: "none", fontWeight: 600, borderRadius: "10px", px: 3 }}
                    >
                      Back to Students
                    </Button>
                  </Box>
                </SectionCard>
              )}

              {/* ════ STUDENT SUMMARY ════ */}
              {studentSummaryCard}

              {/* ════ CLASS FILTERS ════ Order 1: after student summary,
                  before recommendations (order:2). */}
              {!classFirstMode && selectedStudent && hasStudyMode && (
                <SectionCard sx={{ order: 1, scrollMarginTop: 96 }}>
                  <Box sx={{ p: { xs: 2, md: 2.5 } }}>
                    {/* Header: title + active count + clear */}
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1, mb: 2 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                        <Box sx={{ width: 34, height: 34, borderRadius: "9px", bgcolor: alpha(theme.palette.primary.main, 0.08), color: theme.palette.primary.main, display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <FilterAltOutlinedIcon sx={{ fontSize: 18 }} />
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: 15, fontWeight: 700 }}>Class Filters</Typography>
                          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>Narrow the recommended classes below.</Typography>
                        </Box>
                        {activeFilterCount > 0 && (
                          <Chip
                            label={`${activeFilterCount} active`}
                            size="small"
                            sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}
                          />
                        )}
                      </Box>
                      <Button
                        variant="text"
                        size="small"
                        startIcon={<RestartAltIcon sx={{ fontSize: "16px !important" }} />}
                        onClick={clearFilters}
                        disabled={activeFilterCount === 0}
                        sx={{ textTransform: "none", fontWeight: 600, color: "text.secondary" }}
                      >
                        Clear Filters
                      </Button>
                    </Box>

                    {/* Subjects — Match Any when 1 selected, Match All when 2+ selected.
                        The rule is spelled out inline so users know that a second chip
                        NARROWS the recommendations (not broadens them). */}
                    {subjectGRadeDataList.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.disabled", textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.75 }}>
                          Academic Level and Subject
                        </Typography>
                        {/* Grade-aware chips: each option represents an exact
                            grade+subject pair the student is assigned to.
                            A "Grade 4 · Mathematics" chip can never satisfy
                            a "Grade 2 · Mathematics" class. */}
                        {subjectGRadeDataList
                          .filter((item: IGrade) => (item.selected_sub_array?.length ?? 0) > 0)
                          .map((item: IGrade) => (
                          <Box key={`f-g-${item.id}`} sx={{ mb: 1 }}>
                            <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: theme.palette.primary.main, textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.35 }}>
                              {item.grade}
                            </Typography>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                              {item.selected_sub_array?.map((sub: ISubjectGradePerent, idx: number) => (
                                <PillToggle
                                  key={`f-${item.id}-${idx}`}
                                  selected={selectedSubject.includes(sub.id)}
                                  onClick={() => handleSubjectChange(sub.id)}
                                >
                                  {`${item.grade} · ${subjectMap.get(sub.subject_id) || "Subject"}`}
                                </PillToggle>
                              ))}
                            </Box>
                          </Box>
                        ))}
                        <Typography sx={{ mt: 0.75, fontSize: 11, color: "text.secondary", fontStyle: "italic" }}>
                          {selectedSubject.length === 0
                            ? "No combination selected — showing classes matching any of this student's assigned grade + subject combinations."
                            : selectedSubject.length === 1
                              ? "Matches classes containing this exact grade + subject combination."
                              : "Showing classes matching any selected grade + subject combination."}
                        </Typography>
                      </Box>
                    )}

                    {/* Days */}
                    <Box sx={{ mb: 2 }}>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.disabled", textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.75 }}>
                        Days
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                        <PillToggle selected={filterDays.length === 0} onClick={() => setFilterDays([])}>
                          All Days
                        </PillToggle>
                        {Object.values(WeekDays).map((day) => (
                          <PillToggle key={day} selected={filterDays.includes(day)} onClick={() => toggleDay(day)}>
                            {day.slice(0, 3)}
                          </PillToggle>
                        ))}
                      </Box>
                    </Box>

                    {/* Staff / Class Type — two equal columns (Study Mode is
                        auto-applied from the student profile, so no dropdown here) */}
                    <Box
                      sx={{
                        display: "grid",
                        gap: 1.75,
                        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr" },
                      }}
                    >
                      {/* Staff — only teachers with ≥1 class matching the current
                          filters (eligible_staff from the recommended-classes API).
                          "Any Staff" = any eligible teacher. */}
                      {(() => {
                        const noStaff = eligibleStaff.length === 0;
                        const staffOptions = noStaff
                          ? []
                          : [
                              { value: "", label: "Any Staff", count: null as number | null },
                              ...eligibleStaff.map((s) => ({
                                value: String(s.id),
                                label: capitalizeFirstLetter(s.name || "") || `Staff #${s.id}`,
                                count: s.matching_class_count,
                              })),
                            ];
                        const selectedOpt =
                          filterStaffId
                            ? staffOptions.find((o) => o.value === filterStaffId) || null
                            : noStaff
                            ? null
                            : staffOptions[0];
                        return (
                          <Autocomplete
                            size="small"
                            disabled={noStaff}
                            loading={assignClassIsFetching}
                            options={staffOptions}
                            getOptionLabel={(o: any) =>
                              o.count != null
                                ? `${o.label} · ${o.count} matching ${o.count === 1 ? "class" : "classes"}`
                                : o.label
                            }
                            isOptionEqualToValue={(o: any, v: any) => o.value === v.value}
                            value={selectedOpt as any}
                            onChange={(_, v: any) => setFilterStaffId(v?.value || "")}
                            noOptionsText="No matching staff available"
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Staff"
                                placeholder={noStaff ? "No matching staff available" : "Any Staff"}
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: (
                                    <>
                                      {assignClassIsFetching ? <CircularProgress size={16} /> : null}
                                      {params.InputProps.endAdornment}
                                    </>
                                  ),
                                }}
                              />
                            )}
                            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                          />
                        );
                      })()}

                      {/* Class Type */}
                      <FormControl size="small" fullWidth>
                        <InputLabel>Class Type</InputLabel>
                        <Select
                          label="Class Type"
                          value={filterClassType}
                          onChange={(e) => setFilterClassType(e.target.value)}
                          sx={{ borderRadius: "10px" }}
                        >
                          <MenuItem value="">Any Class Type</MenuItem>
                          {Object.values(ClassType).map((ct) => (
                            <MenuItem key={ct} value={ct}>{ct}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Study Mode filter — admin-facing. Default "All" shows
                        every otherwise-eligible class regardless of mode. */}
                    <Box sx={{ mt: 2 }}>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: "text.disabled", textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.75 }}>
                        Study Mode
                      </Typography>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                        <PillToggle selected={filterClassMode === ""} onClick={() => setFilterClassMode("")}>All</PillToggle>
                        <PillToggle selected={filterClassMode === "online"} onClick={() => setFilterClassMode("online")}>Online</PillToggle>
                        <PillToggle selected={filterClassMode === "in_person"} onClick={() => setFilterClassMode("in_person")}>In-Person</PillToggle>
                        <PillToggle selected={filterClassMode === "hybrid"} onClick={() => setFilterClassMode("hybrid")}>Hybrid</PillToggle>
                      </Box>
                    </Box>
                  </Box>
                </SectionCard>
              )}

              {/* ── Compact Billing placeholder ── Shown before any class is
                  selected so the section's role in the flow is discoverable;
                  the real billing UI appears inside the Selected Classes
                  panel once a class is picked. `order: 3` keeps this panel
                  below Recommended Classes in the visual flow regardless of
                  JSX order — see `order` note on the Stack sibling below. */}
              {!classFirstMode && selectedStudent && selectedClasses.length === 0 && (
                <SectionCard sx={{ order: 3, scrollMarginTop: 96 }}>
                  <Box sx={{ p: { xs: 2, md: 2.5 }, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box sx={{
                      width: 36, height: 36, borderRadius: "10px",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      bgcolor: alpha(theme.palette.primary.main, 0.08), color: theme.palette.primary.main,
                    }}>
                      <ReceiptLongIcon sx={{ fontSize: 20 }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: 14, fontWeight: 800 }}>Billing Setup</Typography>
                      <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
                        Select a class to configure billing for this student.
                      </Typography>
                    </Box>
                  </Box>
                </SectionCard>
              )}

              {/* ════ STICKY SELECTED-CLASSES SUMMARY (multi-select only) ════ */}
              {!classFirstMode && selectedStudent && selectedClasses.length > 0 && (
                <Box sx={{ order: 3, scrollMarginTop: 96 }} id="selected-classes-section">
                  <SectionCard
                    sx={{
                      borderColor: hasSelectionConflict ? alpha("#ef4444", 0.45) : alpha(theme.palette.primary.main, 0.35),
                      boxShadow: "0 8px 26px rgba(15,23,42,0.12)",
                    }}
                  >
                    <Box sx={{ p: { xs: 2, md: 2.25 } }}>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1, mb: 1.25 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <PlaylistAddCheckIcon sx={{ fontSize: 20, color: theme.palette.primary.main }} />
                          <Typography sx={{ fontSize: 15, fontWeight: 700 }}>
                            Selected Classes ({selectedClasses.length})
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Chip
                            size="small"
                            icon={hasSelectionConflict
                              ? <WarningAmberIcon sx={{ fontSize: "14px !important", color: "#b45309 !important" }} />
                              : <CheckCircleIcon sx={{ fontSize: "14px !important", color: "#16a34a !important" }} />}
                            label={hasSelectionConflict ? "Schedule conflict" : "No conflicts"}
                            sx={{
                              height: 24, fontSize: 11, fontWeight: 700,
                              bgcolor: hasSelectionConflict ? alpha("#f59e0b", 0.12) : alpha("#22c55e", 0.12),
                              color: hasSelectionConflict ? "#b45309" : "#16a34a",
                              "& .MuiChip-label": { px: 0.75 },
                            }}
                          />
                          <Button
                            size="small"
                            variant="text"
                            startIcon={<DeleteOutlineIcon sx={{ fontSize: "16px !important" }} />}
                            onClick={clearAllSelected}
                            sx={{ textTransform: "none", fontWeight: 600, color: "text.secondary" }}
                          >
                            Clear all
                          </Button>
                        </Box>
                      </Box>

                      {/* Conflict details, when present */}
                      {hasSelectionConflict && (
                        <Box sx={{ mb: 1.25, p: 1.25, borderRadius: "10px", bgcolor: alpha("#f59e0b", 0.08), border: `1px solid ${alpha("#f59e0b", 0.3)}` }}>
                          {selectionConflicts.map((pair, i) => {
                            const nameOf = (c: IClassCreateList) =>
                              Array.from(new Set(c.select_grade_subject_ids.map((id) => assignClassMap.get(id)).filter(Boolean))).join(", ") || `Class #${c.id}`;
                            return (
                              <Typography key={i} sx={{ fontSize: 12, color: "#92400e", fontWeight: 600 }}>
                                “{nameOf(pair.a)}” overlaps “{nameOf(pair.b)}”
                              </Typography>
                            );
                          })}
                        </Box>
                      )}

                      {/* Two-column workspace: selected class list on the
                          left, billing panel for the ACTIVE class on the
                          right. Stacks on mobile. Right panel is sticky
                          on desktop so multi-class billing stays in view
                          while the admin scans through the left list.
                          Business logic unchanged — same BillingSetupSection
                          component, same shared handlers, same configs. */}
                      <Grid container spacing={{ xs: 1.5, md: 2 }} alignItems="flex-start">
                        <Grid item xs={12} md={5} lg={12}>
                          <Stack spacing={1}>
                            {selectedClasses.map((item, idx) => {
                              const liveStaffSel = (item as any).staff as { full_name?: string | null; image?: string | null } | null | undefined;
                              const teamMapStaffSel = teamMap.get(item.staff_id);
                              const staffGet = {
                                name: liveStaffSel?.full_name || teamMapStaffSel?.name || "",
                                image: (liveStaffSel?.image ?? teamMapStaffSel?.image) || "",
                              };
                              const locData = locationMap.get(item.location_id);
                              const { days, startTime, endTime } = getClassScheduleSummary(item.availability);
                              const subjectName =
                                Array.from(new Set(item.select_grade_subject_ids.map((id) => assignClassMap.get(id)).filter(Boolean))).join(", ") || `Class #${item.id}`;
                              const modeRaw = String(item.class_mode || "").toLowerCase();
                              const isOnline = modeRaw === "online";
                              const isHybrid = modeRaw === "hybrid";
                              const scheduleText = `${days.length ? days.map((d) => d.substring(0, 3)).join(", ") + " · " : ""}${startTime && endTime ? `${startTime}–${endTime}` : "No schedule"}`;
                              const modeLoc = `${studyMoodLabel(item.class_mode)}${!isOnline && locData?.location_name ? " · " + locData.location_name : ""}`;
                              const picked = attendanceModes[item.id];
                              const isActive = activeBillingClassId === item.id;
                              const complete = isClassBillingComplete(item.id);
                              const valid = isClassBillingValid(item.id);
                              // Status chip: complete (green) > valid-but-not-completed (amber) > incomplete (grey)
                              const status: { label: string; bg: string; fg: string } =
                                complete
                                  ? { label: "Billing complete", bg: alpha("#22c55e", 0.14), fg: "#15803d" }
                                  : valid
                                  ? { label: "Awaiting confirmation", bg: alpha("#f59e0b", 0.14), fg: "#b45309" }
                                  : { label: "Billing incomplete", bg: alpha("#f59e0b", 0.10), fg: "#b45309" };
                              return (
                                <Box
                                  key={item.id}
                                  role="button"
                                  tabIndex={0}
                                  onClick={() => setActiveBillingClassId(item.id)}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.preventDefault();
                                      setActiveBillingClassId(item.id);
                                    }
                                  }}
                                  sx={{
                                    display: "flex", flexDirection: "column", gap: 0.75,
                                    p: 1.25, borderRadius: "10px",
                                    cursor: "pointer",
                                    transition: "background-color 160ms ease, border-color 160ms ease, box-shadow 160ms ease",
                                    bgcolor: isActive ? alpha(theme.palette.primary.main, 0.06) : "rgba(15,23,42,0.025)",
                                    border: `1px solid ${isActive ? alpha(theme.palette.primary.main, 0.45) : "transparent"}`,
                                    boxShadow: isActive ? `0 2px 10px ${alpha(theme.palette.primary.main, 0.12)}` : "none",
                                    "&:hover": { bgcolor: isActive ? alpha(theme.palette.primary.main, 0.08) : "rgba(15,23,42,0.045)" },
                                  }}
                                >
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                                    <Box sx={{ width: 22, height: 22, borderRadius: "6px", bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800, flexShrink: 0 }}>
                                      {idx + 1}
                                    </Box>
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                      <Typography sx={{ fontSize: 13, fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                        {subjectName}
                                      </Typography>
                                      <Typography sx={{ fontSize: 11.5, color: "text.secondary", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                        {(staffGet?.name || "Staff")} · {scheduleText} · {modeLoc}
                                      </Typography>
                                    </Box>
                                    <Tooltip title="Remove" arrow>
                                      <IconButton
                                        size="small"
                                        onClick={(e) => { e.stopPropagation(); removeSelectedClass(item.id); }}
                                        sx={{ color: "text.disabled", "&:hover": { color: "#ef4444" } }}
                                      >
                                        <CloseIcon sx={{ fontSize: 17 }} />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>

                                  {/* Status row: billing chip + "Editing" pill when active */}
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, pl: 3.75, flexWrap: "wrap" }}>
                                    <Chip
                                      size="small"
                                      label={status.label}
                                      sx={{ height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: status.bg, color: status.fg, "& .MuiChip-label": { px: 0.75 } }}
                                    />
                                    {isActive && (
                                      <Chip
                                        size="small"
                                        label="Editing"
                                        sx={{ height: 20, fontSize: 10, fontWeight: 800, bgcolor: alpha(theme.palette.primary.main, 0.14), color: theme.palette.primary.main, "& .MuiChip-label": { px: 0.75 } }}
                                      />
                                    )}
                                  </Box>

                                  {/* Hybrid class → the admin picks how THIS student attends.
                                      Independent per selected class; not derived from the
                                      student profile. Blocks submit until chosen. The
                                      id below is the scroll target when the user tries
                                      to submit with a missing attendance mode. */}
                                  {isHybrid && (
                                    <Box
                                      id={`assign-attendance-required-${item.id}`}
                                      onClick={(e) => e.stopPropagation()}
                                      sx={{
                                        display: "flex", alignItems: "center", gap: 0.75, pl: 3.75, flexWrap: "wrap",
                                        scrollMarginTop: 96,
                                        ...(picked ? {} : {
                                          p: 0.75, borderRadius: "8px",
                                          bgcolor: alpha("#f59e0b", 0.08),
                                          border: `1px dashed ${alpha("#f59e0b", 0.4)}`,
                                        }),
                                      }}
                                    >
                                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: picked ? "text.secondary" : "#b45309" }}>
                                        Attendance Mode{picked ? ":" : " required:"}
                                      </Typography>
                                      {(["Online", "In Person"] as const).map((m) => (
                                        <PillToggle
                                          key={m}
                                          selected={picked === m}
                                          onClick={() => setAttendanceModes((prev) => ({ ...prev, [item.id]: m }))}
                                        >
                                          {m === "In Person" ? "In-Person" : m}
                                        </PillToggle>
                                      ))}
                                    </Box>
                                  )}
                                </Box>
                              );
                            })}
                          </Stack>
                        </Grid>

                        {/* ── Right column: active-class Billing Setup ──
                            On lg+ this inline panel is hidden — billing lives
                            in the outer sticky sidebar next to Recommended
                            Classes. On sm/md it remains inline so admins on
                            narrower widths still get the side-by-side flow
                            within this section. */}
                        <Grid item xs={12} md={7} lg={8} sx={{ display: { lg: "none" } }}>
                          <Box sx={{ position: { md: "sticky" }, top: { md: 88 } }}>
                            <BillingSetupSection
                              studentId={String(formik.values.student_id ?? "")}
                              classes={
                                activeBillingClassId != null
                                  ? feeModalClasses.filter((c) => c.id === activeBillingClassId)
                                  : feeModalClasses.slice(0, 1)
                              }
                              configs={billingByClassId}
                              onChange={updateBilling}
                              onApplyToAll={applyBillingToAll}
                            />
                          </Box>
                        </Grid>
                      </Grid>

                      {/* Readiness summary — every gate that keeps the final
                          Assign button disabled is spelled out here so admins
                          never see it disabled with no explanation. */}
                      <Box
                        sx={{
                          mt: 1.5,
                          p: 1.5,
                          borderRadius: "12px",
                          bgcolor: allBillingValid && !hasSelectionConflict && attendanceModesComplete ? alpha("#16a34a", 0.06) : alpha("#f59e0b", 0.06),
                          border: `1px solid ${allBillingValid && !hasSelectionConflict && attendanceModesComplete ? alpha("#16a34a", 0.25) : alpha("#f59e0b", 0.3)}`,
                          display: "flex",
                          flexDirection: "column",
                          gap: 0.5,
                        }}
                      >
                        {[
                          {
                            ok: selectedClasses.length > 0,
                            text: `${selectedClasses.length} class${selectedClasses.length === 1 ? "" : "es"} selected`,
                          },
                          {
                            ok: !hasSelectionConflict,
                            text: hasSelectionConflict ? "Resolve schedule conflicts" : "No conflicts",
                          },
                          {
                            ok: allBillingValid,
                            text: `Billing complete (${billingCompleteCount} of ${billingConfigList.length})${
                              billingValidCount > billingCompleteCount
                                ? ` — ${billingValidCount - billingCompleteCount} awaiting Complete Billing Setup`
                                : ""
                            }`,
                          },
                          // Attendance-mode readiness — the previously-missing
                          // row that made the button look mysteriously
                          // disabled. Only shown when there's at least one
                          // Hybrid class in the selection (otherwise nothing
                          // to pick).
                          ...(hybridSelectedClasses.length > 0 ? [{
                            ok: attendanceModesComplete,
                            text: attendanceModesComplete
                              ? `Attendance mode selected (${hybridAttendanceComplete} of ${hybridSelectedClasses.length} Hybrid ${hybridSelectedClasses.length === 1 ? "class" : "classes"})`
                              : `Attendance mode required for ${missingAttendanceCount} Hybrid ${missingAttendanceCount === 1 ? "class" : "classes"}`,
                          }] : []),
                        ].map((row, i) => (
                          <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                            {row.ok ? (
                              <CheckCircleOutlineIcon sx={{ fontSize: 15, color: "#16a34a" }} />
                            ) : (
                              <ErrorOutlineIcon sx={{ fontSize: 15, color: "#b45309" }} />
                            )}
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: row.ok ? "#15803d" : "#b45309" }}>
                              {row.text}
                            </Typography>
                          </Box>
                        ))}
                      </Box>

                      <Button
                        fullWidth
                        variant="contained"
                        onClick={submitInlineAssignment}
                        disabled={!formValid || isAssigningMulti || isAssigningBilling || hasSelectionConflict || !allBillingValid || !attendanceModesComplete}
                        sx={{ mt: 1, textTransform: "none", fontWeight: 700, borderRadius: "10px", py: 1 }}
                      >
                        {(isAssigningMulti || isAssigningBilling) && <CircularProgress size={18} sx={{ mr: 1, color: "inherit" }} />}
                        {selectedClasses.length <= 1
                          ? "Assign Student and Create Billing"
                          : `Assign Student to ${selectedClasses.length} Classes`}
                      </Button>

                      {/* Explicit disabled-reason line — the readiness rows
                          above already list every missing gate, but when the
                          button is greyed out we want a one-line explanation
                          right next to it too. */}
                      {selectedClasses.length > 0 && !attendanceModesComplete && (
                        <Typography sx={{ mt: 0.75, fontSize: 11.5, color: "#b45309", fontWeight: 600, textAlign: "center" }}>
                          Select Online or In-Person attendance mode for {missingAttendanceCount} Hybrid {missingAttendanceCount === 1 ? "class" : "classes"} above.
                        </Typography>
                      )}
                    </Box>
                  </SectionCard>
                </Box>
              )}

              {/* ════ RECOMMENDED CLASSES (hidden in the class-first flow, and
                  when the student has no Study Mode) ════
                  `order: 2` places this above the Selected+Billing panel
                  (order:3) even though it comes later in the JSX — required
                  by the "review, then select, then bill" page flow. */}
              {!classFirstMode && selectedStudent && hasStudyMode && (
                <SectionCard sx={{ order: 2, scrollMarginTop: 96 }}>
                  <Box sx={{ p: { xs: 2, md: 2.5 }, pb: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                      <Box sx={{ width: 36, height: 36, borderRadius: "10px", bgcolor: alpha(theme.palette.primary.main, 0.08), color: theme.palette.primary.main, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <ViewListIcon sx={{ fontSize: 18 }} />
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: 15, fontWeight: 700 }}>Recommended Classes</Typography>
                        <Typography sx={{ fontSize: 12, color: "text.secondary" }}>Best matches for this student</Typography>
                      </Box>
                    </Box>
                    {(() => {
                      // When day filters are active, split the count into
                      // exact (covers every selected day) vs partial (covers
                      // some selected days). Otherwise show the plain count.
                      const total = recommendedClasses.length;
                      if (filterDays.length === 0) {
                        return (
                          <Chip
                            label={`${total} recommended class${total !== 1 ? "es" : ""} found`}
                            size="small"
                            sx={{ height: 24, fontSize: 11.5, fontWeight: 600, bgcolor: "rgba(15,23,42,0.05)", color: "text.secondary" }}
                          />
                        );
                      }
                      const classDaysOf = (c: IClassCreateList) => {
                        const set = new Set<string>();
                        const arr = Array.isArray((c as any).days) && (c as any).days.length > 0
                          ? (c as any).days
                          : getClassScheduleSummary(c.availability).days;
                        arr.forEach((d: string) => set.add(String(d).toLowerCase()));
                        return set;
                      };
                      let exact = 0, partial = 0;
                      recommendedClasses.forEach((c) => {
                        const s = classDaysOf(c as IClassCreateList);
                        const missing = filterDays.filter((d) => !s.has(String(d).toLowerCase()));
                        if (missing.length === 0) exact += 1; else partial += 1;
                      });
                      return (
                        <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                          <Chip
                            label={`${exact} exact match${exact === 1 ? "" : "es"}`}
                            size="small"
                            sx={{ height: 24, fontSize: 11.5, fontWeight: 700, bgcolor: alpha("#16a34a", 0.12), color: "#15803d", border: `1px solid ${alpha("#16a34a", 0.3)}` }}
                          />
                          <Chip
                            label={`${partial} partial`}
                            size="small"
                            sx={{ height: 24, fontSize: 11.5, fontWeight: 700, bgcolor: alpha("#f59e0b", 0.14), color: "#b45309", border: `1px solid ${alpha("#f59e0b", 0.35)}` }}
                          />
                        </Box>
                      );
                    })()}
                  </Box>

                  <Box sx={{ p: { xs: 2, md: 2.5 }, pt: 1 }}>
                    {(assignClassIsLoading || assignClassIsFetching) ? (
                      // Loading skeleton while fetching / re-filtering (no page refresh).
                      <Grid container spacing={2}>
                        {[0, 1, 2, 3].map((i) => (
                          <Grid item xs={12} md={6} key={i}>
                            <Box sx={{ borderRadius: "14px", border: "1px solid rgba(15,23,42,0.08)", p: 1.75 }}>
                              <Box sx={{ display: "flex", gap: 1.25, mb: 1.5, alignItems: "center" }}>
                                <Skeleton variant="circular" width={38} height={38} />
                                <Box sx={{ flex: 1 }}>
                                  <Skeleton width="60%" height={18} />
                                  <Skeleton width="40%" height={14} />
                                </Box>
                              </Box>
                              <Skeleton width="90%" height={16} />
                              <Skeleton width="70%" height={16} />
                              <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                                <Skeleton variant="rounded" width={72} height={22} />
                                <Skeleton variant="rounded" width={72} height={22} />
                              </Box>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    ) : recommendedClasses.length === 0 ? (
                      <Box sx={{ textAlign: "center", py: 5 }}>
                        <SchoolIcon sx={{ fontSize: 44, color: "rgba(15,23,42,0.1)", mb: 1 }} />
                        <Typography sx={{ fontSize: 15, fontWeight: 700, mb: 0.5 }}>
                          {activeFilterCount > 0 ? "No classes match the selected filters." : "No matching classes found for this student."}
                        </Typography>
                        <Typography sx={{ fontSize: 13, color: "text.secondary", mb: 2.5 }}>
                          {activeFilterCount > 0
                            ? "Try adjusting or clearing the filters, or create a new class."
                            : "No active class matches this student's assigned grade and subject combinations. This student can attend classes outside their default school grade — add the required grade and subjects to the student profile, adjust the filters, or create a new class."}
                        </Typography>
                        <Box sx={{ display: "flex", gap: 1.5, justifyContent: "center", flexWrap: "wrap" }}>
                          {activeFilterCount > 0 && (
                            <Button
                              variant="outlined"
                              startIcon={<RestartAltIcon />}
                              onClick={clearFilters}
                              sx={{ textTransform: "none", fontWeight: 600, borderRadius: "10px", px: 3 }}
                            >
                              Clear Filters
                            </Button>
                          )}
                          <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={openCreateClass}
                            sx={{ textTransform: "none", fontWeight: 600, borderRadius: "10px", px: 3 }}
                          >
                            Create New Class
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <Grid container spacing={2}>
                        {/* Section headers so the user never loses track of
                            what's selected — selected cards (preserving the
                            recommendation order they had when picked) float
                            to the top, unselected cards keep their ranked
                            order below. Pure client-side view ordering;
                            no refetch, no payload change. */}
                        {selectedClasses.length > 0 && (
                          <Grid item xs={12} key="__sel_header">
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Typography sx={{ fontSize: 11.5, fontWeight: 700, letterSpacing: "0.05em", color: "text.secondary", textTransform: "uppercase" }}>
                                Selected Classes
                              </Typography>
                              <Chip
                                size="small"
                                label={selectedClasses.length}
                                sx={{ height: 20, fontSize: 11, fontWeight: 800, bgcolor: alpha(theme.palette.primary.main, 0.12), color: theme.palette.primary.main }}
                              />
                            </Box>
                          </Grid>
                        )}
                        {orderedRecommendedClasses.map((item: IClassCreateList, orderedIdx: number) => {
                          const isSelected = selectedClassIds.includes(item.id);
                          const conflict = !isSelected ? conflictByClassId.get(item.id) : undefined;
                          // Inject the "Available Classes" header once, right
                          // before the first unselected card, when both groups
                          // are non-empty.
                          const needsAvailableHeader =
                            selectedClasses.length > 0 &&
                            availableRecommendedClasses.length > 0 &&
                            orderedIdx === selectedClasses.length;
                          const isConflicting = !!conflict;
                          const { days, startTime, endTime } = getClassScheduleSummary(item.availability);
                          // Prefer the LIVE staff record the recommendation API
                          // resolves per class (built from the current Team row).
                          // Falls back to teamMap only for older payloads. This
                          // guarantees the card can never show a stale name from
                          // a cached team list.
                          const liveStaff = (item as any).staff as { full_name?: string | null; image?: string | null } | null | undefined;
                          const teamMapStaff = teamMap.get(item.staff_id);
                          const staffGet = {
                            name: liveStaff?.full_name || teamMapStaff?.name || "",
                            image: (liveStaff?.image ?? teamMapStaff?.image) || "",
                          };
                          const locData = locationMap.get(item.location_id);
                          const locColor = locData?.location_color || theme.palette.primary.main;
                          const studentCount = item.assignStudent?.length || 0;
                          const maxStudents = item.number_of_student;
                          const seatsLeft = maxStudents != null ? Math.max(0, maxStudents - studentCount) : null;
                          const avail = getAvailabilityInfo(studentCount, maxStudents);

                          const reasons = buildMatchReasons(item);
                          const activeReasons = reasons.filter((r) => r.active);
                          // Structured "Subjects by Grade" from the backend (never rebuilt by
                          // flattening the class's grades and subjects arrays). Falls back to
                          // an on-the-fly rollup for older payloads that only send pair-ids.
                          const backendGradeSubjects = item.grade_subjects ?? [];
                          const gradeSubjectRows: Array<{ grade_id: number; grade: string; subjects: string[]; isStudentGrade: boolean }> =
                            backendGradeSubjects.length > 0
                              ? backendGradeSubjects.map((g) => ({
                                  grade_id: g.grade_id,
                                  grade: g.grade,
                                  subjects: (g.subjects || []).map((s) => s?.name).filter(Boolean) as string[],
                                  isStudentGrade: !!selectedStudent?.grade_id && Number(g.grade_id) === Number(selectedStudent.grade_id),
                                }))
                              : (() => {
                                  // Fallback: rollup from pair-id → grade+subject maps.
                                  const buckets = new Map<number, { grade: string; subjects: Set<string> }>();
                                  (item.select_grade_subject_ids || []).forEach((pid) => {
                                    const gid = gradeIdFromSgMap.get(pid);
                                    const gLabel = gradeClassMap.get(pid);
                                    const sLabel = assignClassMap.get(pid);
                                    if (!gid || !gLabel || !sLabel) return;
                                    if (!buckets.has(gid)) buckets.set(gid, { grade: gLabel, subjects: new Set() });
                                    buckets.get(gid)!.subjects.add(sLabel);
                                  });
                                  return Array.from(buckets.entries()).map(([gid, v]) => ({
                                    grade_id: gid,
                                    grade: v.grade,
                                    subjects: Array.from(v.subjects),
                                    isStudentGrade: !!selectedStudent?.grade_id && Number(gid) === Number(selectedStudent.grade_id),
                                  }));
                                })();
                          // Emphasize the student's grade first; other supported grades follow.
                          gradeSubjectRows.sort((a, b) => Number(b.isStudentGrade) - Number(a.isStudentGrade));
                          const studentGradeRow = gradeSubjectRows.find((r) => r.isStudentGrade);

                          // Exactly which SELECTED subjects this class matched (grade-specific,
                          // authoritative from the backend). Shown only when the user is actively
                          // filtering by subject, so we never imply all selected subjects matched.
                          const matchedSubjectNames: string[] = (((item as any).match?.matched_subject_ids ?? []) as number[])
                            .map((sid) => subjectMap.get(sid))
                            .filter(Boolean);

                          // Days may come from the backend (canonical) or be derived from
                          // availability slots; prefer the explicit list.
                          const daysList: string[] = Array.isArray(item.days) && item.days.length > 0
                            ? item.days
                            : days;
                          const showStart = item.start_time || startTime;
                          const showEnd = item.end_time || endTime;

                          // ── Day-match classification ──
                          // The backend returns any class that overlaps at
                          // least one selected day. We split that into "exact"
                          // (class covers every selected day) vs "partial"
                          // (class covers only some) so the user is never told
                          // a Tuesday-only class fully matches Mon/Tue/Wed.
                          // When no day filter is active every result is
                          // treated as "any-day".
                          const classDaySet = new Set(daysList.map((d) => String(d).toLowerCase()));
                          const selectedDayCount = filterDays.length;
                          const matchedSelectedDays = filterDays.filter((d) => classDaySet.has(String(d).toLowerCase()));
                          const missingSelectedDays = filterDays.filter((d) => !classDaySet.has(String(d).toLowerCase()));
                          const dayMatchType: "any" | "exact" | "partial" = selectedDayCount === 0
                            ? "any"
                            : missingSelectedDays.length === 0
                              ? "exact"
                              : "partial";

                          // View Class: use the explicit class_id from the recommendation
                          // (never rely on array index or a generic id). When a next
                          // occurrence exists, pass it so the drawer opens THAT session,
                          // never a stale one from a previous card.
                          const viewClassId = item.class_id ?? item.id;
                          const viewOccurrenceId = item.next_occurrence_id ?? null;
                          const viewClassHref = `/check-staff/classes/list/${viewClassId}${viewOccurrenceId ? `?occurrence_id=${viewOccurrenceId}` : ""}`;

                          return (
                            // Card content (staff row + schedule + View Subjects
                            // + chips + actions) doesn't fit comfortably below
                            // ~320px. The old md={4} / xl={3} breakpoints
                            // produced ~280px cards on common desktop widths
                            // and clipped the availability chip / All-selected
                            // chip / View Subjects button. Two-per-row through
                            // large desktops, three-per-row on xl, so every
                            // card renders wide enough that the internal flex
                            // rows don't have to fight for space.
                            <React.Fragment key={item.id}>
                              {needsAvailableHeader && (
                                <Grid item xs={12} key="__avail_header">
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
                                    <Typography sx={{ fontSize: 11.5, fontWeight: 700, letterSpacing: "0.05em", color: "text.secondary", textTransform: "uppercase" }}>
                                      Available Classes
                                    </Typography>
                                    <Chip
                                      size="small"
                                      label={availableRecommendedClasses.length}
                                      sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: "rgba(15,23,42,0.06)", color: "text.secondary" }}
                                    />
                                  </Box>
                                </Grid>
                              )}
                            <Grid item xs={12} sm={12} md={6} lg={6} xl={4}>
                              <Box
                                ref={(el: HTMLDivElement | null) => { cardRefs.current[item.id] = el; }}
                                onClick={isConflicting
                                  ? () => notifyConflictClick(conflict)
                                  : () => toggleClassSelect(item.id)}
                                aria-disabled={isConflicting || undefined}
                                title={isConflicting
                                  ? "A class at the same time is already selected. Unselect the other class first."
                                  : undefined}
                                sx={{
                                  height: "100%",
                                  display: "flex",
                                  flexDirection: "column",
                                  borderRadius: "14px",
                                  border: isSelected
                                    ? `2px solid ${theme.palette.primary.main}`
                                    : isConflicting
                                      ? `1px solid ${alpha("#f59e0b", 0.35)}`
                                      : "1px solid rgba(15,23,42,0.1)",
                                  bgcolor: isSelected
                                    ? alpha(theme.palette.primary.main, 0.03)
                                    : isConflicting
                                      ? "rgba(15,23,42,0.03)"
                                      : "#fff",
                                  cursor: isConflicting ? "not-allowed" : "pointer",
                                  opacity: isConflicting ? 0.55 : 1,
                                  overflow: "hidden",
                                  transition: "all 0.2s cubic-bezier(0.4,0,0.2,1)",
                                  "&:hover": {
                                    borderColor: isConflicting ? alpha("#f59e0b", 0.35) : theme.palette.primary.main,
                                    boxShadow: isConflicting
                                      ? "none"
                                      : isSelected
                                        ? `0 6px 20px ${alpha(theme.palette.primary.main, 0.15)}`
                                        : "0 4px 14px rgba(15,23,42,0.07)",
                                  },
                                }}
                              >
                                {/* TOP: checkbox + Staff (name FIRST) + selected/availability marker
                                    minWidth:0 chain on the staff block lets the name truncate cleanly
                                    when tight; flexWrap on the outer row is the last-resort for very
                                    narrow viewports so the availability chip drops below the name
                                    instead of overlapping it. */}
                                <Box sx={{ p: 1.75, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", rowGap: 0.75, columnGap: 1 }}>
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0, flex: "1 1 auto" }}>
                                    <Box sx={{ display: "flex", color: isSelected ? theme.palette.primary.main : "text.disabled", flexShrink: 0 }}>
                                      {isSelected ? <CheckBoxIcon sx={{ fontSize: 22 }} /> : <CheckBoxOutlineBlankIcon sx={{ fontSize: 22 }} />}
                                    </Box>
                                    <Avatar src={staffGet?.image || undefined} sx={{ width: 40, height: 40, fontSize: 15, bgcolor: alpha(locColor, 0.15), color: locColor, fontWeight: 700 }}>
                                      {staffGet?.name ? staffGet.name.charAt(0).toUpperCase() : <GroupIcon />}
                                    </Avatar>
                                    <Box sx={{ minWidth: 0 }}>
                                      <Typography sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1.25, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={staffGet?.name || "Staff"}>
                                        {staffGet?.name || "Staff"}
                                      </Typography>
                                      <Typography sx={{ fontSize: 11, color: "text.secondary", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                        {avail.label === "Full" ? "Class full" : "Available"}
                                      </Typography>
                                    </Box>
                                  </Box>
                                  {isSelected ? (
                                    <Chip
                                      icon={<CheckCircleIcon sx={{ fontSize: "15px !important", color: "#fff !important" }} />}
                                      label="Selected"
                                      size="small"
                                      sx={{ height: 24, fontSize: 11, fontWeight: 700, bgcolor: theme.palette.primary.main, color: "#fff", "& .MuiChip-label": { px: 0.75 } }}
                                    />
                                  ) : isConflicting ? (
                                    <Tooltip title={`Conflicts on ${conflict!.day} with ${fmt12h(conflict!.start)} – ${fmt12h(conflict!.end)}`} arrow>
                                      <Chip
                                        label="Time conflict"
                                        size="small"
                                        sx={{ height: 24, fontSize: 11, fontWeight: 700, bgcolor: alpha("#f59e0b", 0.14), color: "#b45309", border: `1px solid ${alpha("#f59e0b", 0.35)}`, "& .MuiChip-label": { px: 0.75 } }}
                                      />
                                    </Tooltip>
                                  ) : (
                                    <Chip
                                      label={avail.label}
                                      size="small"
                                      sx={{ height: 24, fontSize: 11, fontWeight: 700, bgcolor: avail.bg, color: avail.color, "& .MuiChip-label": { px: 0.75 } }}
                                    />
                                  )}
                                </Box>

                                {/* Schedule — directly below staff so time is impossible to miss.
                                    View Subjects lives here on the right, replacing the
                                    formerly-inline Subjects by Grade block below.
                                    flexWrap lets View Subjects + the day-match chip
                                    drop onto their own line when the card is narrow,
                                    instead of squashing the day/time text out. */}
                                <Box sx={{ px: 1.75, pb: 1.25, display: "flex", alignItems: "center", flexWrap: "wrap", rowGap: 0.75, columnGap: 0.75 }}>
                                  <ScheduleIcon sx={{ fontSize: 16, color: theme.palette.primary.main }} />
                                  <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "text.primary", lineHeight: 1.2 }}>
                                      {daysList.length > 0 ? daysList.join(", ") : "No scheduled day"}
                                    </Typography>
                                    <Typography sx={{ fontSize: 12, color: "text.secondary", lineHeight: 1.2 }}>
                                      {showStart && showEnd ? `${fmt12h(showStart)} – ${fmt12h(showEnd)}` : "No schedule"}
                                    </Typography>
                                  </Box>
                                  {gradeSubjectRows.length > 0 && (
                                    <Button
                                      type="button"
                                      size="small"
                                      variant="outlined"
                                      disableElevation
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setOpenSubjectsFor({
                                          title: staffGet.name || "Class",
                                          rows: gradeSubjectRows,
                                        });
                                      }}
                                      startIcon={<MenuBookIcon sx={{ fontSize: "14px !important" }} />}
                                      aria-label="View subjects for this class"
                                      sx={{
                                        // Compact outlined chip-button so the
                                        // control clearly reads as clickable
                                        // (previously it was plain #1D4ED8
                                        // text that looked like a link).
                                        textTransform: "none",
                                        fontWeight: 700, fontSize: 11,
                                        py: 0.35, px: 1,
                                        minWidth: 0, height: 26,
                                        borderRadius: "6px",
                                        color: "#2563EB",
                                        borderColor: "#2563EB",
                                        bgcolor: "#FFFFFF",
                                        flexShrink: 0,
                                        "& .MuiButton-startIcon": { mr: 0.5, ml: 0 },
                                        "&:hover": {
                                          bgcolor: "#EFF6FF",
                                          borderColor: "#1D4ED8",
                                          color: "#1D4ED8",
                                        },
                                        "&:focus-visible": {
                                          outline: "2px solid #1D4ED8",
                                          outlineOffset: 2,
                                        },
                                      }}
                                    >
                                      View Subjects
                                    </Button>
                                  )}
                                  {dayMatchType === "exact" && (
                                    <Chip
                                      label="All selected days"
                                      size="small"
                                      sx={{
                                        height: 22,
                                        fontSize: 10.5,
                                        fontWeight: 700,
                                        bgcolor: alpha("#16a34a", 0.12),
                                        color: "#15803d",
                                        border: `1px solid ${alpha("#16a34a", 0.3)}`,
                                        "& .MuiChip-label": { px: 0.75 },
                                      }}
                                    />
                                  )}
                                  {dayMatchType === "partial" && (
                                    <Chip
                                      label={`${matchedSelectedDays.length} of ${selectedDayCount} days`}
                                      size="small"
                                      sx={{
                                        height: 22,
                                        fontSize: 10.5,
                                        fontWeight: 700,
                                        bgcolor: alpha("#f59e0b", 0.14),
                                        color: "#b45309",
                                        border: `1px solid ${alpha("#f59e0b", 0.35)}`,
                                        "& .MuiChip-label": { px: 0.75 },
                                      }}
                                    />
                                  )}
                                </Box>
                                {dayMatchType === "partial" && (
                                  <Box sx={{ px: 1.75, pb: 1.25, mt: -0.5 }}>
                                    <Typography sx={{ fontSize: 11.5, color: "#b45309", fontWeight: 600 }}>
                                      Only teaches {matchedSelectedDays.join(", ")} · Missing {missingSelectedDays.join(", ")}
                                    </Typography>
                                  </Box>
                                )}
                                {isConflicting && (
                                  <Box sx={{ px: 1.75, pb: 1.25, mt: -0.5, display: "flex", alignItems: "center", gap: 0.5 }}>
                                    <WarningAmberIcon sx={{ fontSize: 14, color: "#b45309" }} />
                                    <Typography sx={{ fontSize: 11.5, color: "#b45309", fontWeight: 600 }}>
                                      {(conflict!.day.charAt(0).toUpperCase() + conflict!.day.slice(1).toLowerCase())}
                                      {" "}{fmt12h(conflict!.start)}–{fmt12h(conflict!.end)} overlaps with a selected class.
                                    </Typography>
                                  </Box>
                                )}

                                {/* MIDDLE: attributes — Subjects by Grade block
                                    removed. View Subjects lives on the right of
                                    the schedule row above so parents can still
                                    open the full grade × subject matrix in one
                                    click. Full compatibility lives in the
                                    Details modal opened from the actions row. */}
                                <Box sx={{ p: 1.75, display: "flex", flexDirection: "column", gap: 1, flex: 1 }}>
                                  {/* Chips: location · mode · type */}
                                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                                    <Chip
                                      icon={<PlaceIcon sx={{ fontSize: "13px !important" }} />}
                                      label={locData?.location_name || "—"}
                                      size="small"
                                      sx={{ height: 22, fontSize: 10.5, fontWeight: 600, bgcolor: alpha(locColor, 0.1), color: locColor, "& .MuiChip-icon": { color: locColor }, "& .MuiChip-label": { px: 0.75 } }}
                                    />
                                    <Chip label={studyMoodLabel(item.class_mode)} size="small" sx={{ height: 22, fontSize: 10.5, fontWeight: 600, bgcolor: "rgba(15,23,42,0.05)", "& .MuiChip-label": { px: 0.75 } }} />
                                    {item.class_type && (
                                      <Chip label={item.class_type} size="small" sx={{ height: 22, fontSize: 10.5, fontWeight: 600, bgcolor: alpha("#6366f1", 0.08), color: "#6366f1", "& .MuiChip-label": { px: 0.75 }, textTransform: "capitalize" }} />
                                    )}
                                  </Box>

                                  {/* Compact match summary — one "Good match"
                                      chip on the card. The full compatibility
                                      breakdown + match-explanation paragraphs
                                      live in the Details modal so the card
                                      stays short enough to render 4-per-row. */}
                                  {activeReasons.length > 0 && (
                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
                                      <Chip
                                        icon={<CheckIcon sx={{ fontSize: "12px !important", color: "#16a34a !important" }} />}
                                        label={activeReasons.length >= 4 ? "Good match" : `${activeReasons.length}/4 match`}
                                        size="small"
                                        sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: alpha("#22c55e", 0.12), color: "#16a34a", "& .MuiChip-label": { px: 0.6 } }}
                                      />
                                    </Box>
                                  )}
                                </Box>

                                {/* BOTTOM: capacity */}
                                <Box
                                  sx={{
                                    px: 1.75,
                                    py: 1.25,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    flexWrap: "wrap",
                                    gap: 1,
                                    bgcolor: "rgba(15,23,42,0.02)",
                                    borderTop: "1px solid rgba(15,23,42,0.05)",
                                  }}
                                >
                                  <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: "text.secondary" }}>
                                    {studentCount}{maxStudents ? `/${maxStudents}` : ""} enrolled
                                    {seatsLeft != null ? ` · ${seatsLeft} left` : ""}
                                  </Typography>
                                  {item.assignStudent?.length > 0 && (
                                    <AvatarGroup max={3} sx={{ "& .MuiAvatar-root": { width: 22, height: 22, fontSize: 9 } }}>
                                      {item.assignStudent.map((student: any) => {
                                        const sData = assignStudentMap.get(student.student_id);
                                        const sName = sData?.name || `${capitalizeFirstLetter(student.student?.first_name || "")} ${capitalizeFirstLetter(student.student?.last_name || "")}`.trim() || "S";
                                        return (
                                          <StudentAvatar
                                            key={student.id}
                                            student={{ id: student.student_id, name: sName, image: sData?.image || student.student?.image || null }}
                                            size={22}
                                            title={sName}
                                          />
                                        );
                                      })}
                                    </AvatarGroup>
                                  )}
                                </Box>

                                {/* ACTIONS */}
                                <Box sx={{ px: 1.75, py: 1.25, display: "flex", gap: 1, borderTop: "1px solid rgba(15,23,42,0.05)" }}>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const detailTitle =
                                        Array.from(new Set(item.select_grade_subject_ids.map((id) => assignClassMap.get(id)).filter(Boolean))).join(", ")
                                        || `Class #${item.id}`;
                                      setOpenDetailsFor({
                                        id: item.id,
                                        title: detailTitle,
                                        staffName: staffGet.name || "Staff",
                                        schedule: `${daysList.length ? daysList.map((d) => d.substring(0, 3)).join(", ") + " · " : ""}${showStart && showEnd ? `${showStart}–${showEnd}` : "No schedule"}`,
                                        location: locData?.location_name || "—",
                                        mode: studyMoodLabel(item.class_mode),
                                        enrollment: item.class_type ?? null,
                                        capacityLine: `${studentCount}${maxStudents ? `/${maxStudents}` : ""} enrolled${seatsLeft != null ? ` · ${seatsLeft} left` : ""}`,
                                        reasons,
                                        rows: gradeSubjectRows,
                                        viewClassHref,
                                        isSelected,
                                        isConflicting,
                                      });
                                    }}
                                    sx={{ flex: 1, textTransform: "none", fontWeight: 600, fontSize: 12.5, borderRadius: "9px" }}
                                  >
                                    Details
                                  </Button>
                                  <Button
                                    size="small"
                                    variant={isSelected ? "contained" : "outlined"}
                                    color="primary"
                                    aria-disabled={isConflicting || undefined}
                                    startIcon={
                                      isSelected
                                        ? <CheckIcon sx={{ fontSize: "15px !important" }} />
                                        : isConflicting
                                          ? <WarningAmberIcon sx={{ fontSize: "15px !important" }} />
                                          : undefined
                                    }
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (isConflicting) { notifyConflictClick(conflict); return; }
                                      toggleClassSelect(item.id);
                                    }}
                                    sx={{
                                      flex: 1,
                                      textTransform: "none",
                                      fontWeight: 600,
                                      fontSize: 12.5,
                                      borderRadius: "9px",
                                      // Amber "disabled but explainable" style used only when the button
                                      // is really a conflict marker — MUI's built-in disabled would
                                      // grey it out and swallow the click, which defeats the toast.
                                      ...(isConflicting && {
                                        cursor: "not-allowed",
                                        color: "#b45309",
                                        bgcolor: alpha("#f59e0b", 0.12),
                                        borderColor: alpha("#f59e0b", 0.45),
                                        "&:hover": {
                                          bgcolor: alpha("#f59e0b", 0.12),
                                          borderColor: alpha("#f59e0b", 0.45),
                                          boxShadow: "none",
                                        },
                                      }),
                                    }}
                                  >
                                    {isSelected
                                      ? "Selected"
                                      : isConflicting
                                        ? "Same-time class already selected"
                                        : "Select"}
                                  </Button>
                                </Box>
                              </Box>
                            </Grid>
                            </React.Fragment>
                          );
                        })}

                        {/* Create New Class — secondary */}
                        <Grid item xs={12} md={6}>
                          <Box
                            onClick={openCreateClass}
                            sx={{
                              height: "100%",
                              minHeight: 120,
                              borderRadius: "14px",
                              border: "1.5px dashed rgba(15,23,42,0.18)",
                              bgcolor: "rgba(15,23,42,0.015)",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                              px: 2.5,
                              transition: "all 0.2s ease",
                              "&:hover": { borderColor: theme.palette.primary.main, bgcolor: alpha(theme.palette.primary.main, 0.02) },
                            }}
                          >
                            <Box sx={{ width: 40, height: 40, borderRadius: "11px", bgcolor: alpha(theme.palette.primary.main, 0.08), display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                              <AddIcon sx={{ fontSize: 22, color: theme.palette.primary.main }} />
                            </Box>
                            <Box>
                              <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: "text.primary" }}>Create New Class</Typography>
                              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>No suitable class? Create one for this student.</Typography>
                            </Box>
                          </Box>
                        </Grid>
                      </Grid>
                    )}
                  </Box>
                </SectionCard>
              )}
            </Stack>
          </Box>

          {/* Desktop-only sticky Billing Setup sidebar. Same
              activeBillingClassId + BillingSetupSection the inline
              Selected Classes panel uses, so both surfaces are always
              in sync. Hidden below lg because the inline billing
              inside Selected Classes covers those breakpoints. */}
          {!classFirstMode && selectedStudent && (
            <Box sx={{
              display: { xs: "none", lg: "block" },
              width: 380,
              flexShrink: 0,
              position: "sticky",
              top: 88,
              alignSelf: "flex-start",
              maxHeight: "calc(100vh - 100px)",
              overflowY: "auto",
            }}>
              <SectionCard>
                <Box sx={{ p: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1.25 }}>
                    <Box sx={{
                      width: 32, height: 32, borderRadius: "9px",
                      bgcolor: alpha(theme.palette.primary.main, 0.08), color: theme.palette.primary.main,
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <ReceiptLongIcon sx={{ fontSize: 18 }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: 14, fontWeight: 800 }}>Billing Setup</Typography>
                      <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>
                        {selectedClasses.length === 0
                          ? "Select a class to configure billing"
                          : `${selectedClasses.length} class${selectedClasses.length === 1 ? "" : "es"} selected · configure per class`}
                      </Typography>
                    </Box>
                  </Box>

                  {selectedClasses.length === 0 ? (
                    <Box sx={{
                      p: 2.5, textAlign: "center",
                      borderRadius: "10px",
                      bgcolor: "rgba(15,23,42,0.02)",
                      border: "1px dashed rgba(15,23,42,0.12)",
                    }}>
                      <PlaylistAddCheckIcon sx={{ fontSize: 36, color: "text.disabled", mb: 0.5 }} />
                      <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
                        No class selected yet. Pick a recommended class on the left to start configuring its billing here.
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      {/* Compact chip switcher — one chip per selected class */}
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1.25 }}>
                        {selectedClasses.map((c, i) => {
                          const isActive = activeBillingClassId === c.id;
                          const complete = isClassBillingComplete(c.id);
                          const label = `#${c.id}`;
                          return (
                            <Chip
                              key={c.id}
                              size="small"
                              label={label}
                              icon={complete
                                ? <CheckCircleIcon sx={{ fontSize: "13px !important", color: (isActive ? "#fff" : "#16a34a") + " !important" }} />
                                : undefined}
                              onClick={() => setActiveBillingClassId(c.id)}
                              sx={{
                                height: 24,
                                fontSize: 11, fontWeight: 700,
                                cursor: "pointer",
                                bgcolor: isActive ? theme.palette.primary.main : (complete ? alpha("#22c55e", 0.12) : "rgba(15,23,42,0.06)"),
                                color: isActive ? "#fff" : (complete ? "#16a34a" : "text.primary"),
                                border: isActive ? "none" : `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                                "&:hover": {
                                  bgcolor: isActive ? theme.palette.primary.main : alpha(theme.palette.primary.main, 0.08),
                                },
                                "& .MuiChip-label": { px: 0.75 },
                                // Also hint the ordinal so admins can map chip → left card
                                "&::before": { content: `"${i + 1}."`, mr: 0.5, fontWeight: 800, opacity: 0.7 },
                              }}
                            />
                          );
                        })}
                      </Box>

                      <BillingSetupSection
                        studentId={String(formik.values.student_id ?? "")}
                        classes={
                          activeBillingClassId != null
                            ? feeModalClasses.filter((c) => c.id === activeBillingClassId)
                            : feeModalClasses.slice(0, 1)
                        }
                        configs={billingByClassId}
                        onChange={updateBilling}
                        onApplyToAll={applyBillingToAll}
                      />
                    </>
                  )}
                </Box>
              </SectionCard>
            </Box>
          )}
          </Box>
        </form>
      </FormikProvider>
      {(isAssigningMulti || isAssigningBilling) && <BackDrop open={isAssigningMulti || isAssigningBilling} />}

      {/* Subjects by Grade — modal. Reuses the exact `gradeSubjectRows`
          the source card computed, so there is zero additional network
          activity. Student's grade (if present) is pinned to the top
          and highlighted. Body scrolls internally so tall lists never
          push the modal past the viewport. */}
      <Dialog
        open={!!openSubjectsFor}
        onClose={() => setOpenSubjectsFor(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "14px", maxHeight: "70vh" } }}
      >
        <DialogTitle sx={{ pb: 1, pr: 5, position: "relative" }}>
          <Typography sx={{ fontSize: 16, fontWeight: 800, color: "text.primary" }}>Subjects by Grade</Typography>
          {openSubjectsFor?.title && (
            <Typography sx={{ fontSize: 12.5, color: "text.secondary", mt: 0.25 }}>
              {openSubjectsFor.title}
            </Typography>
          )}
          {/* Header X — spec requirement. MUI Dialog already handles
              Escape key and click-outside close via `onClose`; this
              adds the visible icon control for pointer users. */}
          <IconButton
            aria-label="Close subjects dialog"
            onClick={() => setOpenSubjectsFor(null)}
            size="small"
            sx={{
              position: "absolute", top: 10, right: 10,
              color: "text.secondary",
              "&:hover": { bgcolor: "rgba(15,23,42,0.06)", color: "text.primary" },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          {!openSubjectsFor || openSubjectsFor.rows.length === 0 ? (
            <Box sx={{ py: 6, textAlign: "center", color: "text.disabled" }}>
              <SchoolIcon sx={{ fontSize: 40, opacity: 0.5, mb: 1 }} />
              <Typography sx={{ fontSize: 13 }}>No grade / subject mappings found.</Typography>
            </Box>
          ) : (
            <Box>
              {/* Header row */}
              <Box sx={{
                display: "grid", gridTemplateColumns: "110px 1fr",
                px: 2, py: 1,
                bgcolor: "rgba(15,23,42,0.03)",
                borderBottom: "1px solid rgba(15,23,42,0.06)",
                fontSize: 10.5, fontWeight: 800, color: "text.secondary",
                textTransform: "uppercase", letterSpacing: "0.08em",
              }}>
                <Box>Grade</Box>
                <Box>Subjects</Box>
              </Box>
              {openSubjectsFor.rows.map((row) => (
                <Box
                  key={row.grade_id}
                  sx={{
                    display: "grid", gridTemplateColumns: "110px 1fr",
                    alignItems: "center",
                    px: 2, py: 1.25,
                    borderBottom: "1px solid rgba(15,23,42,0.05)",
                    "&:last-child": { borderBottom: "none" },
                    bgcolor: row.isStudentGrade ? alpha("#22c55e", 0.06) : "transparent",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                    <Chip
                      label={row.grade}
                      size="small"
                      sx={{
                        height: 22, fontSize: 11.5, fontWeight: 700,
                        bgcolor: row.isStudentGrade ? alpha("#22c55e", 0.14) : "rgba(15,23,42,0.06)",
                        color: row.isStudentGrade ? "#16a34a" : "text.primary",
                        "& .MuiChip-label": { px: 0.75 },
                      }}
                    />
                    {row.isStudentGrade && (
                      <Chip
                        label="Student Grade"
                        size="small"
                        sx={{
                          height: 18, fontSize: 9.5, fontWeight: 700,
                          bgcolor: alpha("#22c55e", 0.14), color: "#16a34a",
                          "& .MuiChip-label": { px: 0.5 },
                        }}
                      />
                    )}
                  </Box>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {row.subjects.length === 0 ? (
                      <Typography sx={{ fontSize: 12, color: "text.disabled" }}>—</Typography>
                    ) : (
                      row.subjects.map((s) => (
                        <Chip
                          key={s}
                          label={s}
                          size="small"
                          sx={{
                            height: 20, fontSize: 11, fontWeight: 600,
                            bgcolor: "rgba(29,78,216,0.08)", color: "#1D4ED8",
                            "& .MuiChip-label": { px: 0.75 },
                          }}
                        />
                      ))
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.25 }}>
          <Button onClick={() => setOpenSubjectsFor(null)} sx={{ textTransform: "none", fontWeight: 700 }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Class Details modal — full breakdown for a recommended class
          (schedule, location, mode, enrollment, capacity, compatibility,
          subjects by grade). Populated at click time from the same
          computed context the card had, so no extra fetch. Keeps the
          card grid compact enough for 4-per-row. */}
      <Dialog
        open={!!openDetailsFor}
        onClose={() => setOpenDetailsFor(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "14px", maxHeight: "80vh" } }}
      >
        {openDetailsFor && (
          <>
            <DialogTitle sx={{ pb: 1 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 800, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.1em" }}>
                Class Details
              </Typography>
              <Typography sx={{ fontSize: 17, fontWeight: 800, color: "text.primary", mt: 0.25 }}>
                {openDetailsFor.title}
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: "text.secondary", mt: 0.25 }}>
                {openDetailsFor.staffName} · Class #{openDetailsFor.id}
              </Typography>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 0 }}>
              {/* Meta grid */}
              <Box sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: 0,
              }}>
                {[
                  { label: "Schedule",   value: openDetailsFor.schedule },
                  { label: "Location",   value: openDetailsFor.location },
                  { label: "Study Mode", value: openDetailsFor.mode },
                  { label: "Enrollment", value: openDetailsFor.enrollment || "—" },
                  { label: "Capacity",   value: openDetailsFor.capacityLine, span: true },
                ].map((row) => (
                  <Box key={row.label} sx={{
                    gridColumn: row.span ? { xs: "auto", sm: "1 / -1" } : "auto",
                    px: 2, py: 1.25,
                    borderBottom: "1px solid rgba(15,23,42,0.05)",
                  }}>
                    <Typography sx={{ fontSize: 10.5, fontWeight: 800, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                      {row.label}
                    </Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.primary", mt: 0.25 }}>
                      {row.value}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* Compatibility */}
              {openDetailsFor.reasons.length > 0 && (
                <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid rgba(15,23,42,0.05)" }}>
                  <Typography sx={{ fontSize: 10.5, fontWeight: 800, color: "text.secondary", textTransform: "uppercase", letterSpacing: "0.08em", mb: 0.75 }}>
                    Compatibility
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {openDetailsFor.reasons.map((r) => (
                      <Chip
                        key={r.label}
                        icon={r.active
                          ? <CheckIcon sx={{ fontSize: "12px !important", color: "#16a34a !important" }} />
                          : <CloseIcon sx={{ fontSize: "12px !important", color: "#9ca3af !important" }} />}
                        label={r.label}
                        size="small"
                        sx={{
                          height: 22, fontSize: 11, fontWeight: 700,
                          bgcolor: r.active ? alpha("#22c55e", 0.10) : "rgba(15,23,42,0.05)",
                          color:   r.active ? "#16a34a" : "#6b7280",
                          "& .MuiChip-label": { px: 0.75 },
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Subjects by Grade */}
              <Box>
                <Box sx={{
                  px: 2, py: 1,
                  borderBottom: "1px solid rgba(15,23,42,0.06)",
                  bgcolor: "rgba(15,23,42,0.02)",
                  fontSize: 10.5, fontWeight: 800, color: "text.secondary",
                  textTransform: "uppercase", letterSpacing: "0.08em",
                }}>
                  Subjects by Grade
                </Box>
                {openDetailsFor.rows.length === 0 ? (
                  <Box sx={{ px: 2, py: 3, textAlign: "center", color: "text.disabled", fontSize: 12.5 }}>
                    No grade / subject mappings found.
                  </Box>
                ) : (
                  openDetailsFor.rows.map((row) => (
                    <Box
                      key={row.grade_id}
                      sx={{
                        display: "grid", gridTemplateColumns: "110px 1fr",
                        alignItems: "center",
                        px: 2, py: 1.25,
                        borderBottom: "1px solid rgba(15,23,42,0.05)",
                        "&:last-child": { borderBottom: "none" },
                        bgcolor: row.isStudentGrade ? alpha("#22c55e", 0.06) : "transparent",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                        <Chip
                          label={row.grade}
                          size="small"
                          sx={{
                            height: 22, fontSize: 11.5, fontWeight: 700,
                            bgcolor: row.isStudentGrade ? alpha("#22c55e", 0.14) : "rgba(15,23,42,0.06)",
                            color: row.isStudentGrade ? "#16a34a" : "text.primary",
                            "& .MuiChip-label": { px: 0.75 },
                          }}
                        />
                        {row.isStudentGrade && (
                          <Chip
                            label="Student Grade"
                            size="small"
                            sx={{ height: 18, fontSize: 9.5, fontWeight: 700, bgcolor: alpha("#22c55e", 0.14), color: "#16a34a", "& .MuiChip-label": { px: 0.5 } }}
                          />
                        )}
                      </Box>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {row.subjects.length === 0 ? (
                          <Typography sx={{ fontSize: 12, color: "text.disabled" }}>—</Typography>
                        ) : (
                          row.subjects.map((s) => (
                            <Chip
                              key={s}
                              label={s}
                              size="small"
                              sx={{ height: 20, fontSize: 11, fontWeight: 600, bgcolor: "rgba(29,78,216,0.08)", color: "#1D4ED8", "& .MuiChip-label": { px: 0.75 } }}
                            />
                          ))
                        )}
                      </Box>
                    </Box>
                  ))
                )}
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 2, py: 1.25, gap: 1 }}>
              <Button
                startIcon={<VisibilityOutlinedIcon sx={{ fontSize: "16px !important" }} />}
                onClick={() => {
                  const href = openDetailsFor.viewClassHref;
                  setOpenDetailsFor(null);
                  navigate(href);
                }}
                sx={{ textTransform: "none", fontWeight: 700 }}
              >
                View Class
              </Button>
              <Box sx={{ flex: 1 }} />
              <Button onClick={() => setOpenDetailsFor(null)} sx={{ textTransform: "none", fontWeight: 700 }}>
                Close
              </Button>
              <Button
                variant="contained"
                disabled={openDetailsFor.isConflicting}
                onClick={() => {
                  if (openDetailsFor.isConflicting) return;
                  const idToToggle = openDetailsFor.id;
                  setOpenDetailsFor(null);
                  toggleClassSelect(idToToggle);
                }}
                sx={{ textTransform: "none", fontWeight: 700, borderRadius: "9px" }}
              >
                {openDetailsFor.isSelected ? "Unselect" : "Select Class"}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default Index;
